import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { DateInput } from "../../components/ui/DateInput";
import ReactDOM from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import AllergiesPanel from "../../components/AllergiesPanel";
import VisitNotesPanel from "../../components/VisitNotesPanel";
import { DEV_DOCTOR_ID, DEV_PATIENT_ID, IS_DEV, VITAL_RANGES } from "../../constants";
import { DiagnosisAPI, DiagnosisApiError, PatientInfo } from "../../services/diagnosis.service";
import { getPatientByDisplayId } from "../../services/patient.service";
import { enqueue } from "../../services/offline-sync.service";
import { saveVisitWithVitals, putAndQueue } from "../../db/operations";
import { db } from "../../db/schema";
import type { SyncFields, VisitRecord, VitalsRecord, PrescriptionRecord, PatientRecord } from "../../db/db.types";
import { unwrapFromStorage, wrapForStorage } from "../../db/encryption-bridge";
import { useOfflineSync } from "../../hooks/useOfflineSync";
import { actionErrorMessage, isBackendUnreachable, isPermissionDenied } from "../../utils/network";
import { AccessDenied } from "../../components/RequirePermission";
import DrugAPI, { DrugSearchResult } from "../../services/drug.service";
import { DocumentAPI, PatientDocument, DocumentCategory } from "../../services/document.service";
import { DocumentUploadModal } from "../../components/patients/documents/DocumentsTab";
import AIScribeConsentModal from "../../components/AIScribeConsentModal";
import { apiRequest } from "../../services/api.client";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useDentalRole } from "../../hooks/useDentalRole";
import { reportChunkLoadFailure } from "../../utils/flow-telemetry";
import { ToothChart, type ToothFinding, mergeDentalBlock, parseDentalFindingsText } from "../../components/ui/ToothChart";
import { DentalChartPanel } from "../../components/dental/DentalChartPanel";
import { TreatmentPlanPanel } from "../../components/dental/TreatmentPlanPanel";
import { useDentalTemplates } from "../../hooks/useDentalTemplates";
import { useClinic } from "../../contexts/ClinicContext";
import { suggestPlan } from "../../utils/dental-plan";
import { mergeRestored, sanitizeFindings } from "../../utils/dental-chart";
import { buildDentalDiagnosis, mergeDiagnosisLine } from "../../utils/dental-diagnosis";
import { useIsSignedInDoctor } from "../../hooks/useSignedInDoctorId";
import type { DentalChart, PlanItem } from "../../schemas/dental.schema";
import { UpgradePromptModal } from "../../components/billing/UpgradePromptModal";
import { FeatureGateChip } from "../../components/billing/FeatureGateChip";

// ─── Helpers ─────────────────────────────────────────────────────────────────
/** Safely convert any Gemini response value to a renderable string.
 *  Gemini sometimes returns objects/arrays instead of plain strings. */
function toStr(val: unknown): string {
  if (val === null || val === undefined || val === 0 || val === false) return "";
  if (typeof val === "string") return val;
  if (Array.isArray(val)) return val.filter(v => v !== 0 && v !== null && v !== undefined).map(v => String(v)).join(", ");
  if (typeof val === "object") return Object.entries(val).filter(([, v]) => v !== 0 && v !== null && v !== undefined).map(([k, v]) => `${k}: ${v}`).join(". ");
  return String(val);
}

// ─── ScribeSpinner — indeterminate circular loader with cycling status ────────
function ScribeSpinner({ messages }: { messages: string[] }) {
  const [msgIdx, setMsgIdx] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setElapsed(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (messages.length <= 1) return;
    const t = setInterval(() => setMsgIdx(i => (i + 1) % messages.length), 4000);
    return () => clearInterval(t);
  }, [messages]);

  return (
    <>
      {/* Circular spinner */}
      <div className="relative w-16 h-16 flex items-center justify-center">
        <svg className="w-16 h-16 animate-spin" viewBox="0 0 50 50" style={{ animationDuration: "1.4s" }}>
          <circle cx="25" cy="25" r="20" fill="none" stroke="#e2e8f0" strokeWidth="4" />
          <circle cx="25" cy="25" r="20" fill="none" stroke="url(#scribe-grad)" strokeWidth="4"
            strokeDasharray="80 126" strokeLinecap="round" />
          <defs>
            <linearGradient id="scribe-grad" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a855f7" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
        </svg>
        <svg className="absolute w-6 h-6 text-purple-500" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
        </svg>
      </div>

      {/* Cycling status message */}
      <p className="text-sm font-semibold text-purple-700 transition-opacity duration-500">{messages[msgIdx]}</p>

      {/* Elapsed timer */}
      <p className="text-xs text-slate-400">{elapsed}s elapsed</p>
    </>
  );
}

// ─── AIScribeModal ────────────────────────────────────────────────────────────
type ScribeStep = "idle" | "selected" | "recording" | "preparing" | "transcribing" | "done" | "error" | "live_recording" | "live_processing";

interface AIScribeResult {
  soap_note: {
    chief_complaint: string;
    history_of_present_illness: string;
    review_of_systems: string | null;
    physical_examination: string | null;
    assessment: string;
    plan: string;
  };
  prescription_document: {
    diagnosis_label: string;
    medications: Array<{
      drug_name: string;
      dosage: string;
      frequency: string;
      timing: string | null;
      duration_days: number;
      total_quantity: number;
      route: string;
      instructions: string | null;
      allergy_conflict: boolean;
    }>;
    investigations: string[];
    advice: string | null;
    follow_up: string | null;
  };
  transcript?: {
    original: string;
    hindi: string;
    english: string;
  };
  meta: { model: string; processing_time_s: number; audio_size_mb: number };
}

const AIScribeModal: React.FC<{
  visitId: string;
  onClose: () => void;
  onApply: (result: AIScribeResult) => void;
}> = ({ visitId, onClose, onApply }) => {
  const [step, setStep] = useState<ScribeStep>("idle");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [result, setResult] = useState<AIScribeResult | null>(null);
  const [error, setError] = useState<string>("");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _patientId = new URLSearchParams(window.location.search).get("patient_id") || "";
  const [transcriptLang, setTranscriptLang] = useState<"original" | "hindi" | "english">("original");
  const [readyAudio, setReadyAudio] = useState<Blob | null>(null); // compressed, ready for Gemini
  const [readySize, setReadySize] = useState("");
  const [recordDuration, setRecordDuration] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [liveInterim, setLiveInterim] = useState("");
  const [liveLang, setLiveLang] = useState<"hi-IN" | "en-IN">("hi-IN");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recognitionRef = useRef<any>(null);
  const liveActiveRef = useRef(false); // tracks whether live mic is active (avoids stale closure)

  const ACCEPTED = ".mp3,.wav,.m4a,.ogg,.webm,.flac,.aac";

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    liveActiveRef.current = false;
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setRecordingBlob(null);
    setReadyAudio(null);
    setStep("preparing");
    setPrepareStage("Decoding audio...");

    // Start compression immediately in background
    try {
      const { compressAudio, needsCompression } = await import("../../utils/audioCompress");
      let finalAudio: Blob = file;
      if (needsCompression(file)) {
        finalAudio = await compressAudio(file, (stage) => {
          setPrepareStage(stage);
          console.log(`[AI Scribe] ${stage}`);
        });
      }
      const sizeMB = (finalAudio.size / (1024 * 1024)).toFixed(1);
      console.log(`[AI Scribe] Ready: ${sizeMB}MB (from ${(file.size / (1024 * 1024)).toFixed(1)}MB)`);
      setReadyAudio(finalAudio);
      setReadySize(sizeMB);
      setStep("selected"); // now Transcribe button is enabled
    } catch (e) {
      // The fallback below hides a failed audioCompress chunk from the user;
      // report it, because it means this tab is running a replaced build.
      reportChunkLoadFailure(e);
      console.warn("[AI Scribe] Compression failed, using original file");
      setReadyAudio(file);
      setReadySize((file.size / (1024 * 1024)).toFixed(1));
      setStep("selected");
    }
  };

  const handleStopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") return;
    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      chunksRef.current = [];
      cleanup();
      setRecordingBlob(blob);
      setSelectedFile(null);
      // Recording is already Opus compressed by the recorder, so it's ready
      setReadyAudio(blob);
      setReadySize((blob.size / (1024 * 1024)).toFixed(1));
      setStep("selected");
    };
    recorder.stop();
  };

  // ── Live Mic handlers (Web Speech API) ──────────────────────────
  const handleStartLiveMic = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Speech recognition not supported in this browser. Use Chrome or Edge.");
      setStep("error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = liveLang;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + " ";
        } else {
          interim = transcript;
        }
      }
      if (final) {
        setLiveTranscript(prev => prev + final);
      }
      setLiveInterim(interim);
    };

    recognition.onerror = (event: any) => {
      console.error("[Live Mic] Speech recognition error:", event.error);
      if (event.error === "not-allowed") {
        setError("Microphone access denied. Please allow microphone in browser settings.");
        setStep("error");
      }
      // "no-speech" and "aborted" are non-fatal — recognition continues
    };

    recognition.onend = () => {
      // Auto-restart if still in live_recording mode (speech recognition stops after silence)
      if (liveActiveRef.current && recognitionRef.current) {
        try { recognitionRef.current.start(); } catch { /* already started */ }
      }
    };

    recognitionRef.current = recognition;
    liveActiveRef.current = true;
    recognition.start();
    setLiveTranscript("");
    setLiveInterim("");
    setRecordDuration(0);
    setStep("live_recording");
    timerRef.current = setInterval(() => setRecordDuration(d => d + 1), 1000);
  };

  const handleStopLiveMic = async () => {
    liveActiveRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    if (recognitionRef.current) {
      recognitionRef.current.onend = null; // prevent auto-restart
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    const fullTranscript = (liveTranscript + " " + liveInterim).trim();
    setLiveInterim("");

    const wordCount = fullTranscript.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount < 20) {
      setError(`Recording too short (${wordCount} words). Please record a longer consultation (at least 20 words) and try again.`);
      setStep("idle");
      setLiveTranscript("");
      return;
    }

    // Send transcript text to Gemini for SOAP structuring
    setStep("live_processing");
    setError("");
    try {
      console.log(`[Live Mic] Sending ${fullTranscript.length} chars to Gemini (visit: ${visitId})`);
      const { runAiScribeFromText } = await import("../../services/ai-scribe.service");
      const res = await runAiScribeFromText(visitId, fullTranscript);
      // Validate SOAP note is a real object with content
      if (!res.soap_note || typeof res.soap_note !== "object" ||
          !(res.soap_note.chief_complaint || res.soap_note.assessment || res.soap_note.plan)) {
        setError("Transcription was too short or unclear. Please try again with a longer, clearer consultation.");
        setStep("idle");
        setLiveTranscript("");
        return;
      }
      console.log("[Live Mic] SUCCESS:", res.meta);
      setResult(res as AIScribeResult);
      setStep("done");
    } catch (e: any) {
      const msg = e?.message || "AI Scribe failed";
      console.error("[Live Mic] FAILED:", msg);
      if (msg.includes("transcript_too_short")) {
        setError("Recording too short. Please record a longer consultation (at least 20 words) and try again.");
        setStep("idle");
        setLiveTranscript("");
      } else if (msg.includes("ai_unavailable")) {
        setError(msg.replace(/[{}'"]/g, "").replace("error: ai_unavailable, message: ", ""));
        setStep("error");
      } else if (msg.includes("feature_not_enabled") || msg.includes("402")) {
        setError("AI Scribe is not enabled for your clinic. Contact admin to upgrade.");
        setStep("error");
      } else {
        setError(msg);
        setStep("error");
      }
    }
  };

  const [prepareStage, setPrepareStage] = useState("");

  const handleTranscribe = async () => {
    if (!readyAudio) return;
    setStep("transcribing");
    setError("");
    try {
      const readySizeMB = readySize || (readyAudio.size / (1024 * 1024)).toFixed(1);
      console.log(`[AI Scribe] Sending ${readySizeMB}MB to Gemini (visit: ${visitId})`);
      const { runAiScribe } = await import("../../services/ai-scribe.service");
      const res = await runAiScribe(visitId, readyAudio, "recording.webm");

      // Validate SOAP note is a real object with content
      if (!res.soap_note || typeof res.soap_note !== "object" ||
          !(res.soap_note.chief_complaint || res.soap_note.assessment || res.soap_note.plan)) {
        setError("Transcription was too short or unclear. Please record a longer, clearer consultation (at least 20 words).");
        setStep("idle");
        setReadyAudio(null);
        setSelectedFile(null);
        return;
      }

      console.log("[AI Scribe] SUCCESS:", res.meta);
      setResult(res as AIScribeResult);
      setStep("done");
    } catch (e: any) {
      const msg = e?.message || "AI Scribe failed";
      console.error("[AI Scribe] FAILED:", msg);
      if (msg.includes("transcript_too_short")) {
        setError("Recording too short. Please record a longer consultation (at least 20 words) and try again.");
        setStep("idle");
        setReadyAudio(null);
        setSelectedFile(null);
      } else if (msg.includes("ai_unavailable")) {
        setError(msg.replace(/[{}'"]/g, "").replace("error: ai_unavailable, message: ", ""));
        setStep("error");
      } else if (msg.includes("feature_not_enabled") || msg.includes("402")) {
        setError("AI Scribe is not enabled for your clinic. Contact admin to upgrade.");
        setStep("error");
      } else {
        setError(msg);
        setStep("error");
      }
    }
  };

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const fileSizeMB = selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(1) : recordingBlob ? (recordingBlob.size / (1024 * 1024)).toFixed(1) : "0";
  const fileName = selectedFile?.name || "recording.webm";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-[460px] mx-4 overflow-hidden max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #a855f7, #ec4899)" }}>
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </div>
            <div>
              <p className="text-base font-bold text-slate-900">AI Scribe Assistant</p>
              <p className="text-sm text-slate-500">Upload or record consultation audio</p>
            </div>
          </div>
          <button onClick={() => { cleanup(); onClose(); }} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors flex-shrink-0">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="px-6 pb-6 flex flex-col gap-5">
          {/* Error banner */}
          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-2xl bg-slate-50 border border-slate-200 px-6 py-8 flex flex-col items-center gap-3">
            {/* IDLE — file upload */}
            {step === "idle" && (
              <>
                <div className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg, #f3e8ff, #fce7f3)" }}>
                  <svg className="w-7 h-7" fill="none" stroke="#a855f7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-800">Upload Voice Note</p>
                  <p className="text-xs text-slate-400 mt-1">Choose an audio file from your laptop</p>
                </div>
                <input ref={fileInputRef} type="file" accept={ACCEPTED} onChange={handleFileChange} className="hidden" />
                <button onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #a855f7, #ec4899)" }}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Choose Audio File
                </button>
                <p className="text-xs text-slate-400">MP3, WAV, M4A, WebM, OGG (Max 200MB)</p>
              </>
            )}

            {/* SELECTED — show file, ready to transcribe */}
            {step === "selected" && (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-800">{fileName}</p>
                  <p className="text-xs text-emerald-600 mt-1">Audio ready · {readySize || fileSizeMB} MB (compressed &amp; noise filtered)</p>
                </div>
                <button onClick={handleTranscribe}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #a855f7, #ec4899)" }}>
                  Transcribe Now
                </button>
                <button onClick={() => { setSelectedFile(null); setRecordingBlob(null); setReadyAudio(null); setStep("idle"); }}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
                  Choose a different file
                </button>
              </>
            )}

            {/* RECORDING — live mic */}
            {step === "recording" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
                  <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-red-600">Recording… {formatTime(recordDuration)}</p>
                <div className="w-full bg-red-100 rounded-full h-1.5 overflow-hidden">
                  <div className="h-1.5 rounded-full bg-red-500 animate-pulse" style={{ width: "100%" }} />
                </div>
                <button onClick={handleStopRecording}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold bg-red-500 hover:bg-red-600 transition">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                  Stop Recording
                </button>
              </>
            )}

            {/* PREPARING — compressing audio */}
            {step === "preparing" && (
              <ScribeSpinner messages={[prepareStage || "Preparing audio…", "Compressing and filtering noise…", "Uploading to server…"]} />
            )}

            {/* LIVE RECORDING — Web Speech API real-time transcript */}
            {step === "live_recording" && (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center animate-pulse">
                  <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                  </svg>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                  <p className="text-sm font-semibold text-emerald-700">Live Listening… {formatTime(recordDuration)}</p>
                </div>

                {/* Language toggle */}
                <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
                  {([["hi-IN", "Hindi"], ["en-IN", "English"]] as const).map(([code, label]) => (
                    <button key={code} onClick={() => {
                      setLiveLang(code);
                      // Must create a NEW recognition instance — Web Speech API
                      // ignores .lang changes on an existing instance.
                      if (recognitionRef.current) {
                        recognitionRef.current.onend = null;
                        recognitionRef.current.stop();
                        setTimeout(() => {
                          const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
                          if (!SR) return;
                          const fresh = new SR();
                          fresh.continuous = true;
                          fresh.interimResults = true;
                          fresh.lang = code;
                          fresh.maxAlternatives = 1;
                          fresh.onresult = (event: any) => {
                            let interim = "";
                            let final = "";
                            for (let i = event.resultIndex; i < event.results.length; i++) {
                              const t = event.results[i][0].transcript;
                              if (event.results[i].isFinal) { final += t + " "; } else { interim = t; }
                            }
                            if (final) setLiveTranscript(prev => prev + final);
                            setLiveInterim(interim);
                          };
                          fresh.onerror = (ev: any) => {
                            console.error("[Live Mic] Speech error:", ev.error);
                          };
                          fresh.onend = () => {
                            if (liveActiveRef.current && recognitionRef.current) {
                              try { recognitionRef.current.start(); } catch {}
                            }
                          };
                          recognitionRef.current = fresh;
                          try { fresh.start(); } catch {}
                        }, 200);
                      }
                    }}
                      className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${liveLang === code ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                      {label}
                    </button>
                  ))}
                </div>

                {/* Live transcript display */}
                <div className="w-full bg-white border border-emerald-200 rounded-xl p-3 text-left min-h-[120px] max-h-[200px] overflow-y-auto">
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wide mb-1.5">Live Transcript</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {liveTranscript}
                    {liveInterim && <span className="text-slate-400 italic">{liveInterim}</span>}
                    {!liveTranscript && !liveInterim && (
                      <span className="text-slate-400 italic">Start speaking… text will appear here in real-time</span>
                    )}
                  </p>
                </div>

                <button onClick={handleStopLiveMic}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>
                  Stop &amp; Generate SOAP
                </button>
                <p className="text-[10px] text-slate-400">Click stop when consultation is finished</p>
              </>
            )}

            {/* LIVE PROCESSING — converting transcript to SOAP */}
            {step === "live_processing" && (
              <ScribeSpinner messages={["Generating SOAP Note…", "Structuring prescription…", "Almost there…"]} />
            )}

            {/* TRANSCRIBING — Gemini processing */}
            {step === "transcribing" && (
              <ScribeSpinner messages={["Transcribing audio…", "Extracting SOAP note…", "Structuring prescription…", "Almost there…"]} />
            )}

            {/* DONE — show results */}
            {step === "done" && result && (
              <>
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-emerald-500" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-emerald-700">AI Scribe Complete</p>
                <p className="text-xs text-slate-400">{result.meta.processing_time_s}s · {result.meta.model}</p>

                {/* Transcript with language tabs */}
                {result.transcript && (
                  <div className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-left">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[10px] font-bold text-slate-600 uppercase tracking-wide">Transcript</p>
                      <div className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5">
                        {([["original", "Original"], ["hindi", "हिंदी"], ["english", "English"]] as const).map(([key, label]) => (
                          <button key={key} onClick={() => setTranscriptLang(key)}
                            className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-all ${transcriptLang === key ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="max-h-32 overflow-y-auto text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                      {result.transcript[transcriptLang] || "—"}
                    </div>
                  </div>
                )}

                {/* SOAP summary — structured S / O / A / P */}
                <div className="w-full text-left space-y-2">
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wide">SOAP Note</p>

                  {/* Guard: if soap_note is not an object (e.g. Gemini returned 0 or null), show raw or error */}
                  {(!result.soap_note || typeof result.soap_note !== "object") ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      <p className="text-xs text-amber-700">
                        SOAP note could not be parsed. Raw response: {JSON.stringify(result.soap_note)}
                      </p>
                      <p className="text-[10px] text-amber-500 mt-1">Try recording a longer, clearer consultation.</p>
                    </div>
                  ) : null}

                  {/* S — Subjective */}
                  {result.soap_note && typeof result.soap_note === "object" && (result.soap_note.chief_complaint || result.soap_note.history_of_present_illness || result.soap_note.review_of_systems) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] font-bold flex items-center justify-center">S</span>
                        <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">Subjective</span>
                      </div>
                      {result.soap_note.chief_complaint && (
                        <p className="text-xs text-slate-700"><span className="font-semibold text-slate-500">Chief Complaint:</span> {toStr(result.soap_note.chief_complaint)}</p>
                      )}
                      {result.soap_note.history_of_present_illness && (
                        <p className="text-xs text-slate-700 mt-1"><span className="font-semibold text-slate-500">HPI:</span> {toStr(result.soap_note.history_of_present_illness)}</p>
                      )}
                      {result.soap_note.review_of_systems && (
                        <p className="text-xs text-slate-700 mt-1"><span className="font-semibold text-slate-500">ROS:</span> {toStr(result.soap_note.review_of_systems)}</p>
                      )}
                    </div>
                  )}

                  {/* O — Objective */}
                  {result.soap_note && typeof result.soap_note === "object" && result.soap_note.physical_examination && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-green-500 text-white text-[10px] font-bold flex items-center justify-center">O</span>
                        <span className="text-[10px] font-bold text-green-700 uppercase tracking-wide">Objective</span>
                      </div>
                      <p className="text-xs text-slate-700"><span className="font-semibold text-slate-500">Physical Exam:</span> {toStr(result.soap_note.physical_examination)}</p>
                    </div>
                  )}

                  {/* A — Assessment */}
                  {result.soap_note && typeof result.soap_note === "object" && result.soap_note.assessment && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">A</span>
                        <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wide">Assessment</span>
                      </div>
                      <p className="text-xs text-slate-700"><span className="font-semibold text-slate-500">Diagnosis:</span> {toStr(result.soap_note.assessment)}</p>
                    </div>
                  )}

                  {/* P — Plan */}
                  {((result.soap_note && typeof result.soap_note === "object" && result.soap_note.plan) || result.prescription_document?.advice || result.prescription_document?.follow_up || result.prescription_document?.investigations?.length) && (
                    <div className="bg-violet-50 border border-violet-200 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className="w-5 h-5 rounded-full bg-violet-500 text-white text-[10px] font-bold flex items-center justify-center">P</span>
                        <span className="text-[10px] font-bold text-violet-700 uppercase tracking-wide">Plan</span>
                      </div>
                      {result.soap_note && typeof result.soap_note === "object" && result.soap_note.plan && (
                        <p className="text-xs text-slate-700"><span className="font-semibold text-slate-500">Treatment:</span> {toStr(result.soap_note.plan)}</p>
                      )}
                      {result.prescription_document.investigations?.length > 0 && (
                        <p className="text-xs text-slate-700 mt-1"><span className="font-semibold text-slate-500">Investigations:</span> {toStr(result.prescription_document.investigations)}</p>
                      )}
                      {result.prescription_document.advice && (
                        <p className="text-xs text-slate-700 mt-1"><span className="font-semibold text-slate-500">Advice:</span> {toStr(result.prescription_document.advice)}</p>
                      )}
                      {result.prescription_document.follow_up && (
                        <p className="text-xs text-slate-700 mt-1"><span className="font-semibold text-slate-500">Follow-up:</span> {toStr(result.prescription_document.follow_up)}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Medications */}
                {result.prescription_document.medications.length > 0 && (
                  <div className="w-full bg-violet-50 border border-violet-200 rounded-xl p-3 text-left">
                    <p className="text-[10px] font-bold text-violet-600 uppercase tracking-wide mb-1">
                      Prescription · {result.prescription_document.diagnosis_label}
                    </p>
                    {result.prescription_document.medications.map((med, i) => (
                      <div key={i} className={`text-xs py-1 ${i > 0 ? "border-t border-violet-100" : ""} ${med.allergy_conflict ? "text-red-700 bg-red-50 -mx-1 px-1 rounded" : "text-slate-700"}`}>
                        <span className="font-medium">{med.drug_name}</span> {med.dosage} · {med.frequency} {med.timing ? `(${med.timing})` : ""} · {med.duration_days}d = {med.total_quantity} qty
                        {med.allergy_conflict && <span className="ml-1 text-[10px] font-bold text-red-600">⚠ ALLERGY</span>}
                      </div>
                    ))}
                  </div>
                )}

                <button onClick={() => { if (result) onApply(result); }}
                  className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                  Apply to SOAP Note
                </button>
              </>
            )}

            {/* ERROR — retry */}
            {step === "error" && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-7 h-7 text-red-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-red-700">Transcription Failed</p>
                <button onClick={() => { setStep("idle"); setError(""); setSelectedFile(null); setRecordingBlob(null); }}
                  className="text-xs text-violet-600 hover:text-violet-800 underline">Try again</button>
              </>
            )}
          </div>

          {/* Mic options — show in idle and error states */}
          {(step === "idle" || step === "error") && (
            <div className="flex flex-col items-center gap-4 w-full">
              <div className="flex items-center gap-4">
                {/* Live Mic (Web Speech API — real-time) */}
                <div className="flex flex-col items-center gap-2">
                  <button onClick={handleStartLiveMic}
                    className="w-20 h-20 rounded-full flex items-center justify-center transition-all hover:scale-105 shadow-lg"
                    style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                    <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                      <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                    </svg>
                  </button>
                  <div className="text-center">
                    <p className="text-xs font-semibold text-emerald-700">Live Mic</p>
                    <p className="text-[10px] text-slate-400">Real-time text</p>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </div>

  );
};

// ─── Header icons ─────────────────────────────────────────────────────────────
const IcStethoscope = () => (
  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
    <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" /><circle cx="20" cy="10" r="2" />
  </svg>
);
const IcSparkle = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09z" />
    <path d="M18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456z" />
  </svg>
);
const IcSave = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
  </svg>
);
const IcSend = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M22 2 11 13" /><path d="M22 2 15 22 11 13 2 9l20-7z" />
  </svg>
);
const IcCheckCircle = () => (
  <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const IcInfoCircle = () => (
  <svg className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ─── Vital icons (cyan, w-5 h-5) ─────────────────────────────────────────────
const VIcHeart = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const VIcThermometer = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
  </svg>
);
const VIcPulse = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const VIcDroplet = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);
const VIcWind = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
  </svg>
);
const VIcScale = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10" /><line x1="12" y1="3" x2="12" y2="21" />
    <path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" />
  </svg>
);
const VIcBloodDrop = () => (
  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    <path d="M12 12v4" /><path d="M10 14h4" />
  </svg>
);

// ─── Types & data ─────────────────────────────────────────────────────────────
interface VitalEntry {
  label: string;
  value: string;
  unit: string;
  icon: React.ReactNode;
  status: "normal" | "high" | "low";
  placeholder?: string;
  readonly?: boolean;
}

const VITALS: VitalEntry[] = [
  { label: "Heart Rate",    value: "", unit: "bpm",   icon: <VIcHeart />,       status: "normal", placeholder: "72"     },
  { label: "Temperature",   value: "", unit: "°F",    icon: <VIcThermometer />, status: "normal", placeholder: "98.6"   },
  { label: "Blood Pressure",value: "", unit: "mmHg",  icon: <VIcPulse />,       status: "normal", placeholder: "120/80" },
  { label: "SpO2",          value: "", unit: "%",     icon: <VIcDroplet />,     status: "normal", placeholder: "98"     },
  { label: "Respiration",   value: "", unit: "/min",  icon: <VIcWind />,        status: "normal", placeholder: "16"     },
  { label: "Height",        value: "", unit: "cm",    icon: <VIcScale />,       status: "normal", placeholder: "170"    },
  { label: "Weight",        value: "", unit: "kg",    icon: <VIcScale />,       status: "normal", placeholder: "70"     },
  { label: "Head Circumference", value: "", unit: "cm", icon: <VIcScale />,     status: "normal", placeholder: "45"     },
  { label: "BMI",           value: "", unit: "kg/m²", icon: <VIcScale />,       status: "normal", placeholder: "—",     readonly: true },
  { label: "Blood Sugar",   value: "", unit: "mg/dL", icon: <VIcBloodDrop />,   status: "normal", placeholder: "90"     },
];

type VitalValidator = {
  /** Strip characters that are never valid for this field */
  filter: (v: string) => string;
  /** Derive status badge from the current value */
  getStatus: (v: string) => VitalEntry["status"];
};

/** Clamp a numeric string: allow typing but cap the parsed value at max. */
const clampInt = (v: string, max: number) => {
  const d = v.replace(/[^\d]/g, "");
  if (!d) return "";
  return parseInt(d) > max ? String(max) : d;
};
const clampDec = (v: string, max: number) => {
  const d = v.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1");
  if (!d || d === ".") return d;
  return parseFloat(d) > max ? String(max) : d;
};

const VITAL_VALIDATORS: VitalValidator[] = [
  // Heart Rate — max 250 bpm
  {
    filter: v => clampInt(v, 250),
    getStatus: v => {
      const n = parseInt(v);
      if (!v || isNaN(n)) return "normal";
      if (n > 100) return "high";
      if (n < 60)  return "low";
      return "normal";
    },
  },
  // Temperature — max 113°F (45°C)
  {
    filter: v => clampDec(v, 113),
    getStatus: v => {
      const n = parseFloat(v);
      if (!v || isNaN(n)) return "normal";
      if (n > 99.5) return "high";
      if (n < 96.8) return "low";
      return "normal";
    },
  },
  // Blood Pressure — systolic max 300, diastolic max 200
  {
    filter: v => {
      const cleaned = v.replace(/[^\d/]/g, "");
      const slashIdx = cleaned.indexOf("/");
      if (slashIdx === -1) return clampInt(cleaned, 300);
      return `${clampInt(cleaned.slice(0, slashIdx), 300)}/${clampInt(cleaned.slice(slashIdx + 1), 200)}`;
    },
    getStatus: v => {
      if (!/^\d+\/\d+$/.test(v)) return "normal";
      const [sys, dia] = v.split("/").map(Number);
      if (sys > 140 || dia > 90) return "high";
      if (sys < 90  || dia < 60) return "low";
      return "normal";
    },
  },
  // SpO2 — max 100%
  {
    filter: v => clampInt(v, 100),
    getStatus: v => {
      const n = parseInt(v);
      if (!v || isNaN(n)) return "normal";
      if (n < 94) return "low";
      return "normal";
    },
  },
  // Respiration — max 60 breaths/min
  {
    filter: v => clampInt(v, 60),
    getStatus: v => {
      const n = parseInt(v);
      if (!v || isNaN(n)) return "normal";
      if (n > 20) return "high";
      if (n < 12) return "low";
      return "normal";
    },
  },
  // Height — max 280 cm
  {
    filter: v => clampDec(v, 280),
    getStatus: () => "normal" as const,
  },
  // Weight — max 350 kg
  {
    filter: v => clampDec(v, 350),
    getStatus: () => "normal" as const,
  },
  // BMI — readonly, auto-calculated
  {
    filter: v => v,
    getStatus: v => {
      const n = parseFloat(v);
      if (!v || isNaN(n)) return "normal";
      if (n > 30)   return "high";
      if (n < 18.5) return "low";
      return "normal";
    },
  },
  // Blood Sugar — max 600 mg/dL
  {
    filter: v => clampInt(v, 600),
    getStatus: v => {
      const n = parseInt(v);
      if (!v || isNaN(n)) return "normal";
      if (n > 140) return "high";
      if (n < 70)  return "low";
      return "normal";
    },
  },
];

const STATUS_STYLE: Record<VitalEntry["status"], string> = {
  normal: "text-emerald-600 bg-emerald-50 border-emerald-200",
  high:   "text-red-600 bg-red-50 border-red-200",
  low:    "text-amber-600 bg-amber-50 border-amber-200",
};

// ─── Vitals Overview panel ────────────────────────────────────────────────────
interface VitalsOverviewProps {
  vitals: VitalEntry[];
  onUpdate: (index: number, value: string) => void;
}
const VitalsOverview: React.FC<VitalsOverviewProps> = ({ vitals, onUpdate }) => {
  const hasAnyValue = vitals.some(v => v.value.trim() !== "");
  const allNormal   = vitals.every(v => v.status === "normal");

  const [tooltip, setTooltip] = useState<{ label: string; rect: DOMRect } | null>(null);

  const showTooltip = (label: string, e: React.MouseEvent<HTMLSpanElement>) => {
    if (!VITAL_RANGES[label]) return;
    setTooltip({ label, rect: e.currentTarget.getBoundingClientRect() });
  };
  const hideTooltip = () => setTooltip(null);

  const TOOLTIP_W = 176; // w-44 = 11rem = 176px

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 pt-4 pb-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[15px] font-bold text-slate-900">Vitals Overview</h2>
        {hasAnyValue && allNormal && (
          <span className="text-xs font-medium text-emerald-600 border border-emerald-300 bg-emerald-50 rounded-full px-3 py-1">
            All Normal
          </span>
        )}
      </div>

      {/* Vital rows */}
      {vitals.map((v, i) => (
        <div
          key={v.label}
          className={`flex items-center gap-3 py-3 ${i < vitals.length - 1 ? "border-b border-slate-100" : ""}`}
        >
          {/* Icon box */}
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center flex-shrink-0">
            {v.icon}
          </div>

          {/* Label + editable value */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 leading-tight">{v.label}</p>
            {v.readonly ? (
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="text-lg font-bold text-slate-400 leading-tight min-w-[2rem]">
                  {v.value || "—"}
                </span>
                <span className="text-xs font-medium text-cyan-500">{v.unit}</span>
              </div>
            ) : v.label === "Blood Pressure" ? (() => {
              const parts = v.value.split("/");
              const sys = parts[0] ?? "";
              const dia = parts.length > 1 ? parts[1] : "";
              return (
                <div className="flex items-baseline gap-0.5 mt-0.5">
                  <input
                    type="text"
                    value={sys}
                    placeholder="120"
                    maxLength={3}
                    onChange={e => onUpdate(i, e.target.value.replace(/[^\d]/g, "").slice(0, 3) + "/" + dia)}
                    className="w-10 text-lg font-bold text-slate-800 leading-tight bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-cyan-400 focus:outline-none transition-colors text-center"
                  />
                  <span className="text-base font-bold text-slate-400 select-none">/</span>
                  <input
                    type="text"
                    value={dia}
                    placeholder="80"
                    maxLength={3}
                    onChange={e => onUpdate(i, sys + "/" + e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
                    className="w-10 text-lg font-bold text-slate-800 leading-tight bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-cyan-400 focus:outline-none transition-colors text-center"
                  />
                  <span className="text-xs font-medium text-cyan-500 ml-1">{v.unit}</span>
                </div>
              );
            })() : (
              <div className="flex items-baseline gap-1 mt-0.5">
                <input
                  type="text"
                  value={v.value}
                  placeholder={v.placeholder}
                  maxLength={["Temperature", "Height", "Weight", "Head Circumference"].includes(v.label) ? 5 : 3}
                  onChange={e => onUpdate(i, e.target.value)}
                  className="w-20 text-lg font-bold text-slate-800 leading-tight bg-transparent border-b-2 border-transparent hover:border-slate-200 focus:border-cyan-400 focus:outline-none transition-colors"
                />
                <span className="text-xs font-medium text-cyan-500">{v.unit}</span>
              </div>
            )}
          </div>

          {/* Status badge — hover triggers portal tooltip */}
          <div className="flex-shrink-0">
            <span
              className={`text-xs font-medium border rounded-full px-2.5 py-0.5 cursor-default ${STATUS_STYLE[v.status]}`}
              onMouseEnter={e => showTooltip(v.label, e)}
              onMouseLeave={hideTooltip}
            >
              {v.status}
            </span>
          </div>
        </div>
      ))}

      {/* Portal tooltip — renders at document.body, escapes all overflow containers */}
      {tooltip && VITAL_RANGES[tooltip.label] && ReactDOM.createPortal(
        <div
          className="pointer-events-none"
          style={{
            position: "fixed",
            top:  tooltip.rect.top - 8,
            left: Math.min(
              tooltip.rect.right - TOOLTIP_W,
              window.innerWidth - TOOLTIP_W - 8,
            ),
            transform: "translateY(-100%)",
            zIndex: 9999,
          }}
        >
          <div className="bg-slate-900 text-white text-[11px] rounded-xl px-3 py-2.5 shadow-2xl border border-slate-700/60" style={{ width: TOOLTIP_W }}>
            <p className="font-semibold text-slate-100 mb-2">
              {tooltip.label} <span className="font-normal text-slate-400">({VITAL_RANGES[tooltip.label].unit})</span>
            </p>
            <div className="flex flex-col gap-1">
              {VITAL_RANGES[tooltip.label].rows.map(r => (
                <div key={r.label} className="flex items-center justify-between gap-3">
                  <span className={`font-medium ${r.color} w-12`}>{r.label}</span>
                  <span className="text-slate-300 tabular-nums">{r.value}</span>
                </div>
              ))}
            </div>
            {/* Arrow */}
            <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900" />
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

// ─── Chief Complaint panel ────────────────────────────────────────────────────
const IcSearch = () => (
  <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
  </svg>
);
const IcMic = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

const IcTooth = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C9 2 6 4 6 7c0 2 1 3.5 1.5 5.5C8 15 8.5 22 12 22s4-7 4.5-9.5C17 10.5 18 9 18 7c0-3-3-5-6-5z" />
  </svg>
);

interface ChiefComplaintPanelProps {
  value: string;
  onChange: (v: string) => void;
  onScribeClick?: () => void;
  onDentalFindingClick?: () => void;
}
const ChiefComplaintPanel: React.FC<ChiefComplaintPanelProps> = ({ value, onChange, onScribeClick, onDentalFindingClick }) => (
  <div className="w-full bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
    {/* Title row */}
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <IcSearch />
        <h2 className="text-[15px] font-bold text-slate-800">Chief Complaint &amp; Symptoms</h2>
      </div>
      {onDentalFindingClick && (
        <button
          onClick={onDentalFindingClick}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-semibold border border-sky-200 transition-colors"
        >
          <IcTooth />
          Dental Finding
        </button>
      )}
    </div>

    {/* Input area */}
    <div className="bg-slate-100 rounded-xl p-3 flex flex-col gap-2">
      <textarea
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          // Auto-expand: reset height then set to scrollHeight (capped by max-height in CSS)
          e.target.style.height = "auto";
          e.target.style.height = e.target.scrollHeight + "px";
        }}
        placeholder="Describe symptoms..."
        rows={3}
        className="w-full bg-transparent text-base sm:text-sm text-slate-700 outline-none placeholder:text-slate-400 resize-none leading-relaxed"
        style={{ minHeight: "4.5rem", maxHeight: "24rem", overflowY: "auto" }}
      />
      {onScribeClick && (
        <div className="flex justify-end">
          <button onClick={onScribeClick} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors">
            <span className="text-white"><IcMic /></span>
            AI Scribe
          </button>
        </div>
      )}
    </div>
  </div>
);


interface DiagnosisEntry {
  name: string;
  icd: string;
  rationale: string;
  confidence: number;
  confidenceColor: string;
}

interface SelectedDiagnosesPanelProps {
  diagnoses: DiagnosisEntry[];
  onRemove: (dx: DiagnosisEntry) => void;
}

const SelectedDiagnosesPanel: React.FC<SelectedDiagnosesPanelProps> = ({ diagnoses, onRemove }) => {
  if (diagnoses.length === 0) return null;

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 rounded-md bg-blue-100 flex items-center justify-center">
          <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2 className="text-[15px] font-bold text-slate-800">Selected Diagnoses</h2>
      </div>
      <div className="flex flex-col gap-3">
        {diagnoses.map((dx) => (
          <div key={dx.icd} className="border border-blue-200 bg-blue-50/50 rounded-xl p-3 flex flex-col gap-2.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-bold text-slate-800 leading-snug">{dx.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">ICD:<br />{dx.icd}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <button
                  onClick={() => onRemove(dx)}
                  className="px-3 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-xs font-semibold flex items-center gap-1"
                >
                  <IcX /> Remove
                </button>
              </div>
            </div>
            <div className="bg-white rounded-lg px-3 py-2 text-xs text-slate-600 border border-slate-100">
              {dx.rationale}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Clinical Notes panel ─────────────────────────────────────────────────────
const IcFileText = () => (
  <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" />
  </svg>
);
const IcMicTeal = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
    <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
    <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
  </svg>
);

interface ClinicalNotesPanelProps {
  value: string;
  onChange: (v: string) => void;
}
const ClinicalNotesPanel: React.FC<ClinicalNotesPanelProps> = ({ value, onChange }) => {
  const [isRecording, setIsRecording] = useState(false);
  const recognitionRef = useRef<any>(null);

  const toggleVoiceInput = () => {
    if (isRecording) {
      // Stop recording
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      setIsRecording(false);
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser. Please use Chrome or Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-IN"; // English (India)

    let finalTranscript = "";

    recognition.onresult = (event: any) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += t + " ";
        } else {
          interim = t;
        }
      }
      onChange((finalTranscript + interim).trim());
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsRecording(false);
      recognitionRef.current = null;
      if (event.error === "not-allowed") {
        alert("Microphone access denied. Please allow microphone permission in your browser settings (click the lock icon in the address bar) and try again.");
      } else if (event.error === "audio-capture") {
        alert("No microphone detected. Please connect a microphone and try again.");
      } else if (event.error === "network") {
        alert("Network error during voice recognition. Please check your internet connection.");
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    setIsRecording(true);
    try {
      recognition.start();
    } catch (e) {
      console.error("Failed to start recognition:", e);
      setIsRecording(false);
      recognitionRef.current = null;
      alert("Failed to start voice input. Please try again.");
    }
  };

  return (
    <div className="w-full bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <IcFileText />
          <h2 className="text-[15px] font-bold text-slate-800">Diagnosis</h2>
        </div>
        <button
          onClick={toggleVoiceInput}
          className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
            isRecording
              ? "text-red-500 animate-pulse"
              : "text-cyan-600 hover:text-cyan-700"
          }`}
        >
          <IcMicTeal />
          {isRecording ? "Stop Recording" : "Voice Input"}
        </button>
      </div>

      {/* Textarea */}
      <div className="bg-slate-100 rounded-xl px-3 py-3">
        <textarea
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
          }}
          placeholder="Additional observations, treatment plan, patient education..."
          rows={3}
          className="w-full bg-transparent text-base sm:text-sm text-slate-700 outline-none placeholder:text-slate-400 resize-none leading-relaxed"
          style={{ minHeight: "4.5rem", maxHeight: "24rem", overflowY: "auto" }}
        />
      </div>
    </div>
  );
};

// ─── Right-panel icons ────────────────────────────────────────────────────────
const IcZap = () => (
  <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const IcCalendar = () => (
  <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);
const IcTestTube = () => (
  <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14.5 2 20 7.5l-9.88 9.88a2.43 2.43 0 0 1-3.44-3.44z" />
    <path d="M10 6 4 12" /><path d="m7.01 17.87-1.17 1.17a2.43 2.43 0 0 1-3.43-3.43l1.17-1.17" />
  </svg>
);
const IcPill = () => (
  <svg className="w-4 h-4 text-slate-700 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
    <path d="m8.5 8.5 7 7" />
  </svg>
);
const IcX = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const IcPlus = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24">
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IcEdit = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    <path d="m15 5 4 4" />
  </svg>
);
const IcCheck = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ─── Medicine Kits panel ──────────────────────────────────────────────────────
const KITS = ["Fever Kit", "Cold & Flu", "Diabetes Set", "Pain Relief"];
const MedicineKitsPanel: React.FC = () => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <IcPill />
        <h2 className="text-[15px] font-bold text-slate-800">Medicine Kits</h2>
      </div>
      <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
        Coming Soon
      </span>
    </div>
    <div className="grid grid-cols-2 gap-2">
      {KITS.map((kit) => (
        <button
          key={kit}
          disabled
          className="border border-slate-100 rounded-xl py-3 px-3 text-sm font-medium text-slate-400 bg-slate-50 cursor-not-allowed text-left"
        >
          {kit}
        </button>
      ))}
    </div>
  </div>
);

// ─── Medicines panel ──────────────────────────────────────────────────────────
interface MedicationItem {
  id: number;
  drug_id?: string;
  name: string;
  dosage: string;
  frequency: string;
  route: string;
  duration: string;
  quantity: string;
  instructions: string;
}

// ─── Add Medicine Modal ───────────────────────────────────────────────────────
interface AddMedicineModalProps {
  onClose: () => void;
  onAdd: (med: Omit<MedicationItem, "id">) => void;
  initialData?: MedicationItem | null;
}

const EMPTY_MED = { name: "", dosage: "", frequency: "", route: "", duration: "", quantity: "", instructions: "" };
const DEFAULT_DOSAGE_UNITS = [
  "mg", "mcg", "g", "ml", "IU", "%",
  "tablet", "capsule", "drops", "puffs", "sachet",
];

interface ParsedDosage {
  value: string;
  unit: string;
  hasSpace: boolean;
}

function parseDosage(dosageStr: string | null | undefined): ParsedDosage | null {
  if (!dosageStr || dosageStr === "N/A") return null;
  const trimmed = dosageStr.trim();
  const m = trimmed.match(/^(\d+\.?\d*)\s*(.*)$/);
  if (!m) return null;
  const value = m[1];
  const unit = m[2] ? m[2].trim() : "";
  const hasSpace = /\s/.test(trimmed.slice(value.length));
  return { value, unit, hasSpace };
}

function formatDosage(value: string, unit: string, hasSpace = false): string {
  const v = value.trim();
  const u = unit.trim();
  if (!u) return v;
  if (!v) return "";
  if (v.toLowerCase().endsWith(u.toLowerCase())) {
    return v;
  }
  const isWordUnit = /^[a-zA-Z]{2,}/.test(u) && !["mg", "mcg", "ml", "iu"].includes(u.toLowerCase());
  if (hasSpace || isWordUnit || u.includes("/")) {
    return `${v} ${u}`;
  }
  return `${v}${u}`;
}

// Defense-in-depth: validate drug_id is a UUID before sending it back to the
// server. Backend already enforces this, but a corrupted search response or a
// future bug in the picker should not be able to put garbage on the wire.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v: unknown): v is string => typeof v === "string" && UUID_RE.test(v);

const FREQUENCY_MAX_LEN = 50;

function parseStrength(strength: string | null | undefined): { value: string; unit: string; hasSpace?: boolean } | null {
  return parseDosage(strength);
}

const AddMedicineModal: React.FC<AddMedicineModalProps> = ({ onClose, onAdd, initialData }) => {
  const isEditing = Boolean(initialData);

  const parsedInitialDosage = React.useMemo(() => {
    return parseDosage(initialData?.dosage);
  }, [initialData?.dosage]);

  const [form, setForm]             = React.useState(() => ({
    name:         initialData?.name         ?? "",
    dosage:       parsedInitialDosage ? parsedInitialDosage.value : (initialData?.dosage ?? ""),
    frequency:    initialData?.frequency    ?? "",
    route:        initialData?.route        ?? "",
    duration:     initialData?.duration     ?? "",
    quantity:     initialData?.quantity     ?? "",
    instructions: initialData?.instructions ?? "",
  }));
  const [dosageUnit, setDosageUnit] = React.useState(() => {
    if (parsedInitialDosage) {
      return parsedInitialDosage.unit;
    }
    return "mg";
  });
  const [hasDosageSpace, setHasDosageSpace] = React.useState(() => {
    return parsedInitialDosage ? parsedInitialDosage.hasSpace : false;
  });

  const unitOptions = React.useMemo(() => {
    const list = [...DEFAULT_DOSAGE_UNITS];
    if (dosageUnit && !list.includes(dosageUnit)) {
      list.push(dosageUnit);
    }
    return list;
  }, [dosageUnit]);

  const [selectedDrugId, setSelectedDrugId] = React.useState<string | undefined>(initialData?.drug_id);
  const [suggestions, setSuggestions] = React.useState<DrugSearchResult[]>([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [errors, setErrors] = React.useState<{ name?: string; dosage?: string; frequency?: string }>({});
  const searchTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbortRef = React.useRef<AbortController | null>(null);
  const nameWrapRef = React.useRef<HTMLDivElement>(null);

  // Close suggestions when clicking outside
  React.useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (nameWrapRef.current && !nameWrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Cancel any in-flight drug search when the modal unmounts. Without this,
  // closing the modal mid-debounce / mid-fetch causes setState-on-unmounted
  // warnings and a wasted network round-trip.
  React.useEffect(() => {
    return () => {
      if (searchTimeout.current)  clearTimeout(searchTimeout.current);
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setForm(prev => ({ ...prev, name: val }));
    // Clear selectedDrugId when user types over the name so manual edits
    // without re-selecting from autocomplete dropdown do not carry stale drug_id
    setSelectedDrugId(undefined);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (val.trim().length < 3) { setSuggestions([]); setShowSuggestions(false); return; }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    searchTimeout.current = setTimeout(async () => {
      try {
        const results = await DrugAPI.searchDrugs(val.trim(), { signal: controller.signal });
        setSuggestions(results);
        setShowSuggestions(results.length > 0);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 350);
  };

  const handleSelectSuggestion = (drug: DrugSearchResult) => {
    const brandPart = drug.drug_name !== drug.generic_name ? ` (${drug.drug_name})` : "";
    setForm(prev => ({ ...prev, name: `${drug.generic_name}${brandPart}` }));
    setSelectedDrugId(drug.drug_id);
    setSuggestions([]);
    setShowSuggestions(false);

    // Auto-populate dosage value + unit from strength/unit fields
    const parsed = parseStrength(drug.strength);
    if (parsed) {
      setForm(prev => ({ ...prev, dosage: parsed.value }));
      const unit = drug.unit || parsed.unit || "mg";
      setDosageUnit(unit);
      setHasDosageSpace(parsed.hasSpace || false);
    } else if (drug.unit) {
      setDosageUnit(drug.unit);
    }
  };

  const set = (field: keyof typeof EMPTY_MED) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = () => {
    const errs: typeof errors = {};
    if (!form.name.trim())      errs.name      = "Required";
    if (!form.dosage.trim())    errs.dosage    = "Required";
    if (!form.frequency.trim()) errs.frequency = "Required";
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onAdd({
      ...form,
      drug_id:   isUuid(selectedDrugId) ? selectedDrugId : undefined,
      dosage:    formatDosage(form.dosage, dosageUnit, hasDosageSpace),
      frequency: form.frequency.trim(),
    });
    onClose();
  };

  const labelCls = "text-xs font-medium text-slate-600";
  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-base sm:text-sm text-slate-800 outline-none focus:border-blue-400 transition-colors placeholder:text-slate-400";
  const errCls   = "text-xs text-red-500 mt-0.5";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-3xl shadow-2xl w-full max-w-[480px] mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <IcPill />
            </div>
            <p className="text-base font-bold text-slate-900">
              {isEditing ? "Edit Medicine" : "Add Medicine"}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors">
            <IcX />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">

          {/* Name — autocomplete, required */}
          <div className="flex flex-col gap-1" ref={nameWrapRef}>
            <label className={labelCls}>Medicine Name <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                value={form.name}
                onChange={handleNameChange}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                placeholder="Search brand or generic name…"
                autoComplete="off"
                className={inputCls}
              />
              {showSuggestions && (
                <ul className="absolute z-10 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {suggestions.map(drug => (
                    <li
                      key={drug.drug_id}
                      onMouseDown={() => handleSelectSuggestion(drug)}
                      className="px-3 py-2.5 cursor-pointer hover:bg-blue-50 flex items-center justify-between gap-2"
                    >
                      <span className="text-sm text-slate-800 font-medium truncate">
                        {drug.generic_name}
                        {drug.drug_name !== drug.generic_name && (
                          <span className="text-slate-400 font-normal"> · {drug.drug_name}</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0">
                        {drug.strength}
                        {drug.is_controlled_substance && (
                          <span className="ml-1.5 text-red-500 font-semibold">CD</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {errors.name && <p className={errCls}>{errors.name}</p>}
          </div>

          {/* Dosage + Frequency — required */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelCls}>Dosage <span className="text-red-500">*</span></label>
              <div className="flex gap-1.5">
                <input
                  value={form.dosage}
                  onChange={e => setForm(prev => ({ ...prev, dosage: e.target.value }))}
                  placeholder="e.g. 500"
                  className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 transition-colors placeholder:text-slate-400"
                />
                <select
                  aria-label="Dosage unit"
                  value={dosageUnit}
                  onChange={e => setDosageUnit(e.target.value)}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-2 py-2 text-sm text-slate-800 outline-none focus:border-blue-400 transition-colors"
                >
                  {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                  <option value="">(none)</option>
                </select>
              </div>
              {errors.dosage && <p className={errCls}>{errors.dosage}</p>}
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelCls}>Frequency <span className="text-red-500">*</span></label>
              <input
                value={form.frequency}
                onChange={set("frequency")}
                placeholder="e.g. Twice daily, 1-0-1"
                maxLength={FREQUENCY_MAX_LEN}
                className={inputCls}
              />
              {errors.frequency && <p className={errCls}>{errors.frequency}</p>}
            </div>
          </div>

          {/* Route + Duration */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className={labelCls}>Route</label>
              <select value={form.route} onChange={set("route")} className={inputCls}>
                <option value="">Select…</option>
                <option>Oral</option>
                <option>Topical</option>
                <option>Intravenous</option>
                <option>Intramuscular</option>
                <option>Subcutaneous</option>
                <option>Inhalation</option>
                <option>Sublingual</option>
                <option>Rectal</option>
                <option>Ophthalmic</option>
                <option>Otic</option>
                <option>Nasal</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className={labelCls}>Duration</label>
              <input value={form.duration} onChange={set("duration")} placeholder="e.g. 7 days" className={inputCls} />
            </div>
          </div>

          {/* Quantity */}
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Quantity</label>
            <input
              type="number"
              min={1}
              value={form.quantity}
              onChange={set("quantity")}
              placeholder="Number of units to dispense"
              className={inputCls}
            />
          </div>

          {/* Instructions */}
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Patient Instructions</label>
            <textarea
              value={form.instructions}
              onChange={set("instructions")}
              placeholder="e.g. Take after meals with water"
              rows={3}
              className={`${inputCls} resize-none leading-relaxed`}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-semibold hover:opacity-90 transition-opacity bg-gradient-to-br from-blue-500 to-sky-400 shadow-sm"
          >
            {isEditing ? <IcCheck /> : <IcPlus />}
            {isEditing ? "Save Changes" : "Add Medicine"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Lab Tests panel ──────────────────────────────────────────────────────────
interface LabTestsPanelProps {
  value: string;
  onChange: (v: string) => void;
}
const LabTestsPanel: React.FC<LabTestsPanelProps> = ({ value, onChange }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <IcTestTube />
      <h2 className="text-[15px] font-bold text-slate-800">Lab Tests</h2>
    </div>
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="CBC, Blood Sugar..."
      className="w-full bg-slate-100 rounded-xl px-3 py-2.5 text-base sm:text-sm text-slate-700 outline-none placeholder:text-slate-400"
    />
  </div>
);

// ─── Advice panel ─────────────────────────────────────────────────────────────
interface AdvicePanelProps {
  value: string;
  onChange: (v: string) => void;
}
const AdvicePanel: React.FC<AdvicePanelProps> = ({ value, onChange }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <IcZap />
      <h2 className="text-[15px] font-bold text-slate-800">Advice</h2>
    </div>
    <div className="bg-slate-100 rounded-xl px-3 py-3">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Rest, hydration..."
        rows={3}
        className="w-full bg-transparent text-base sm:text-sm text-slate-700 outline-none placeholder:text-slate-400 resize-none leading-relaxed"
      />
    </div>
  </div>
);

// ─── Follow-Up panel ──────────────────────────────────────────────────────────
interface FollowUpPanelProps {
  value: string;
  onChange: (v: string) => void;
}
const FollowUpPanel: React.FC<FollowUpPanelProps> = ({ value, onChange }) => {
  const today = new Date().toISOString().split("T")[0];
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <IcCalendar />
        <h2 className="text-[15px] font-bold text-slate-800">Follow-Up</h2>
      </div>
      <DateInput
        value={value}
        onChange={(e) => {
          const picked = e.target.value;
          if (picked && picked < today) return;
          onChange(picked);
        }}
        min={today}
        className="w-full bg-slate-100 rounded-xl px-3 py-2.5 text-sm text-slate-400 outline-none"
      />
    </div>
  );
};

// ─── Constants ────────────────────────────────────────────────────────────────
const BLUE_GRADIENT = "bg-gradient-to-br from-blue-500 to-sky-400";

/** Format a YYYY-MM-DD date string → "15 June 2026" for PDF/prescription display. */
function fmtFollowUpDate(d: string): string {
  try {
    const dt = new Date(d + "T00:00:00");
    return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
}

function calcAge(dob: string): number {
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function fmtPatientId(uuid: string): string {
  // Show first segment of UUID as display ID, e.g. "PAT-44444444"
  return `PAT-${uuid.split("-")[0].toUpperCase()}`;
}

// ─── NinetysDiagnosis ─────────────────────────────────────────────────────────

/** Map vitals state array → VitalsPayload for the API */
function buildVitalsPayload(vitals: VitalEntry[]): import("../../services/diagnosis.service").VitalsPayload {
  const get = (label: string) => vitals.find(v => v.label === label)?.value ?? "";
  const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? undefined : n; };
  const bp = get("Blood Pressure").split("/");
  return {
    blood_pressure_systolic:  num(bp[0] ?? ""),
    blood_pressure_diastolic: num(bp[1] ?? ""),
    heart_rate:      num(get("Heart Rate")),
    respiratory_rate: num(get("Respiration")),
    temperature:     num(get("Temperature")),
    temperature_unit: "Fahrenheit",
    oxygen_saturation: num(get("SpO2")),
    blood_sugar:     num(get("Blood Sugar")),
    bmi:             num(get("BMI")),
    height:          num(get("Height")),
    height_unit:     "cm",
    weight:          num(get("Weight")),
    weight_unit:     "kg",
    head_circumference: num(get("Head Circumference")),
    head_circumference_unit: "cm",
  };
}

// ─── VisitDocumentsPanel ──────────────────────────────────────────────────────

function mimeEmoji(mime: string): string {
  if (mime.startsWith("image/")) return "🖼️";
  if (mime === "application/pdf") return "📄";
  if (mime.includes("dicom") || mime === "application/octet-stream") return "🩻";
  return "📎";
}

const VisitDocumentsPanel: React.FC<{ patientId: string | null; visitId: string | null }> = ({
  patientId,
  visitId,
}) => {
  const [docs, setDocs] = useState<PatientDocument[]>([]);
  const [categories, setCategories] = useState<DocumentCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const reload = useCallback(() => {
    if (!patientId || !visitId) return;
    setLoading(true);
    setFetchError(false);
    DocumentAPI.list(patientId, { visit_id: visitId })
      .then(setDocs)
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false));
  }, [patientId, visitId]);

  useEffect(() => {
    if (!patientId || !visitId) {
      setDocs([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFetchError(false);
    DocumentAPI.list(patientId, { visit_id: visitId })
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch(() => { if (!cancelled) setFetchError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, visitId]);

  useEffect(() => {
    if (!patientId) return;
    DocumentAPI.listCategories(patientId).then(setCategories).catch(() => {});
  }, [patientId]);

  const openDoc = async (doc: PatientDocument) => {
    if (!patientId) return;
    try {
      const { url } = await DocumentAPI.getUrl(patientId, doc.document_id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      alert("Could not open document. Please try again.");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-bold text-slate-800">Visit Documents</h2>
        {visitId && patientId && (
          <button
            type="button"
            onClick={() => setShowUpload(true)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2.5 py-1 rounded-lg transition-colors"
          >
            + Upload
          </button>
        )}
      </div>
      {!visitId ? (
        <p className="text-xs text-slate-400">Save the visit first to attach documents.</p>
      ) : loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : fetchError ? (
        <p className="text-xs text-red-500">Failed to load documents. Check your connection.</p>
      ) : docs.length === 0 ? (
        <p className="text-xs text-slate-400">No documents attached to this visit.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {docs.map((doc) => (
            <li key={doc.document_id}>
              <button
                onClick={() => openDoc(doc)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-50 transition-colors text-left"
              >
                <span className="text-base leading-none">{mimeEmoji(doc.mime_type)}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-medium text-slate-700 truncate">{doc.file_name}</span>
                  {doc.is_sensitive && (
                    <span className="inline-block mt-0.5 px-1.5 py-px rounded text-[10px] font-semibold bg-amber-50 text-amber-700">Sensitive</span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showUpload && patientId && visitId && (
        <DocumentUploadModal
          patientId={patientId}
          visitId={visitId}
          categories={categories}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); }}
        />
      )}
    </div>
  );
};

const NinetysDiagnosis: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Fall back to seed IDs in development so the page is navigable without params.
  const rawPatientId = searchParams.get("patient_id") || (IS_DEV ? DEV_PATIENT_ID : "");
  const doctorId     = searchParams.get("doctor_id")  || (IS_DEV ? DEV_DOCTOR_ID  : "");
  const isPatientDisplayId = rawPatientId.toUpperCase().startsWith("PAT-") && !rawPatientId.includes("?");
  const [patientId, setPatientId] = useState(isPatientDisplayId ? "" : rawPatientId);

  const [chiefComplaint, setChiefComplaint] = useState("");
  const [notes, setNotes] = useState("");
  const [advice, setAdvice] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [labTests, setLabTests] = useState("");
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [vitals, setVitals] = useState<VitalEntry[]>(() => VITALS.map(v => ({ ...v })));
  const [medications, setMedications] = useState<MedicationItem[]>([]);
  const [selectedDiagnoses, setSelectedDiagnoses] = useState<DiagnosisEntry[]>([]);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "queued" | "error">("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Server refused a load (403) — holds its reason; null means "not refused".
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [visitId, setVisitId] = useState<string | null>(() => searchParams.get("visit_id"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScribeModal, setShowScribeModal] = useState(false);
  const [showScribeConsent, setShowScribeConsent] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showAddMedicine, setShowAddMedicine] = useState(false);
  const [editingMedicine, setEditingMedicine] = useState<MedicationItem | null>(null);

  // Feature gating for AI Scribe
  const { getGateState, getFlag, isAdmin } = useFeatureFlags();
  const aiScribeGate = getGateState("ai_scribe");
  const aiScribeFlag = getFlag("ai_scribe");
  const aiScribeLocked = aiScribeGate === "locked" || aiScribeGate === "expired";
  const aiScribeHidden = aiScribeGate === "locked";

  // Dental role gate — only dentists (Dentistry / Orthodontics) see dental UI
  const { isDentist } = useDentalRole(doctorId || undefined);
  const [showDentalFinding, setShowDentalFinding] = useState(false);
  const [toothFindings, setToothFindings] = useState<ToothFinding[]>([]);
  // Set while restoring a saved visit: the stored text already holds the
  // dental block, so regenerating it would rewrite the text and auto-save.
  const skipDentalMergeRef = useRef(false);

  // Inline chart + treatment plan for every dentist; the popup chart is now
  // only the read-only view on the patient profile.
  const dentalPlanOn = isDentist;
  const { activeClinicId } = useClinic();
  // Templates belong to the signed-in doctor; offer them only on that doctor's
  // own visits, so an admin charting for a colleague never mixes the two.
  const isOwnVisit = useIsSignedInDoctor(doctorId);
  const canUseTemplates = dentalPlanOn && isOwnVisit;
  const dentalTemplates = useDentalTemplates(canUseTemplates, activeClinicId, doctorId);
  const [dentition, setDentition] = useState<DentalChart["dentition"]>("permanent");
  const [planItems, setPlanItems] = useState<PlanItem[]>([]);
  // A reopened draft starts with an empty chart until the visit loads. An
  // empty chart clears the stored one, so nothing is sent until the restore
  // has run. If the visit cannot be loaded at all, the chart is never sent and
  // the stored one stays as it was.
  const [dentalLoaded, setDentalLoaded] = useState(() => !searchParams.get("visit_id"));
  // Set when the reopened visit could not be loaded from the server or the
  // device. Charting still works; the stored chart is fetched again when the
  // connection returns, merged with what was charted meanwhile, and only then
  // is the chart saved, so neither side is lost.
  const [dentalRestoreFailed, setDentalRestoreFailed] = useState(false);
  // Mirrors dentalRestoreFailed for the restore callback. A late recovery
  // merges into a session already charted, whose Diagnosis line must then be
  // replaced rather than assumed to be in the saved text.
  const dentalRestoreFailedRef = useRef(false);
  useEffect(() => { dentalRestoreFailedRef.current = dentalRestoreFailed; }, [dentalRestoreFailed]);
  // Findings rebuilt from hand-edited text can hold invalid or repeated
  // teeth; the server refuses those and the whole visit save would fail.
  const dentalFindings = useMemo(() => sanitizeFindings(toothFindings), [toothFindings]);

  // Rebuild suggestions whenever the chart or the favourites change. The
  // engine keeps the dentist's own rows and the ids of surviving ones; an
  // unchanged result keeps the old array so auto-save does not fire.
  useEffect(() => {
    if (!dentalPlanOn) return;
    setPlanItems((prev) => {
      const next = suggestPlan({ findings: dentalFindings, current: prev, favourites: dentalTemplates.favourites });
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dentalPlanOn, toothFindings, dentalTemplates.favourites]);

  // Sent on every save while the feature is on, so clearing the chart clears
  // it on the server too. Undefined (not sent) when off: the server then
  // leaves any stored chart untouched.
  const dentalChartPayload: DentalChart | undefined = useMemo(() => (dentalPlanOn && dentalLoaded ? {
    version: 1,
    dentition,
    findings: dentalFindings,
    plan_items: planItems,
    completes: [],
  } : undefined), [dentalPlanOn, dentalLoaded, dentition, dentalFindings, planItems]);

  // Restore from the structured chart, which supersedes the text block.
  // Merge, not replace: anything charted before the stored chart arrived
  // (a late or retried load) is kept alongside it. On a normal reopen nothing
  // has been charted yet, so this is the stored chart as it was.
  const restoreDentalChart = useCallback((chart: DentalChart | null | undefined) => {
    setDentalLoaded(true);
    setDentalRestoreFailed(false);
    if (!chart) return;
    if (!dentalRestoreFailedRef.current) skipDentalMergeRef.current = true;
    setToothFindings((prev) => mergeRestored(chart, { findings: sanitizeFindings(prev), plan_items: [] }).findings as ToothFinding[]);
    setPlanItems((prev) => mergeRestored(chart, { findings: [], plan_items: prev }).plan_items);
    setDentition(chart.dentition);
  }, []);

  // Retry a failed restore when the device comes back online, and every 30 s.
  useEffect(() => {
    const vid = searchParams.get("visit_id");
    if (!dentalPlanOn || !dentalRestoreFailed || !vid) return;
    const retry = () => {
      DiagnosisAPI.getVisit(vid).then((res) => restoreDentalChart(res.visit.dental_chart)).catch(() => { /* try again later */ });
    };
    window.addEventListener("online", retry);
    const timer = window.setInterval(retry, 30_000);
    return () => { window.removeEventListener("online", retry); window.clearInterval(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dentalPlanOn, dentalRestoreFailed, restoreDentalChart]);

  // When the chart changes, write its diagnosis line into the Diagnosis box
  // ("Irreversible pulpitis wrt 36; Caries wrt 16") and keep the doctor's own
  // words around it. Chief Complaint stays the patient's words: the old
  // "Dental Findings" block, still on visits saved before this release, is
  // removed from it the first time the chart changes. Findings print as their
  // own section from visits.dental_chart.
  const diagnosisLineRef = useRef("");
  useEffect(() => {
    const line = buildDentalDiagnosis(sanitizeFindings(toothFindings));
    if (skipDentalMergeRef.current) {
      // Restored visit: its saved Diagnosis text already holds this line.
      skipDentalMergeRef.current = false;
      diagnosisLineRef.current = line;
      return;
    }
    setChiefComplaint((prev) => mergeDentalBlock(prev, []));
    setNotes((prev) => mergeDiagnosisLine(prev, diagnosisLineRef.current, line));
    diagnosisLineRef.current = line;
  }, [toothFindings]);

  // Restore chief complaint and the tooth findings encoded in it. Without the
  // findings, the next chart tap would rebuild the block from that one tooth
  // and silently drop every tooth charted earlier.
  const restoreChiefComplaint = useCallback((text: string) => {
    setChiefComplaint(text);
    const findings = parseDentalFindingsText(text);
    if (findings.length === 0) return;
    skipDentalMergeRef.current = true;
    setToothFindings(findings);
  }, []);

  const handleUpgradeClick = () => navigate("/settings", { state: { section: "finance" } });
  const handleNotifyAdmin = () => {
    window.location.href = "mailto:?subject=Upgrade%20Kaagazy%20EMR%20Subscription&body=Hi%2C%20please%20upgrade%20our%20clinic%20subscription%20to%20continue%20using%20AI%20Scribe.";
  };
  const [toast, setToast] = useState<{ message: string; fields: string[] } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Offline sync ─────────────────────────────────────────────────────────────
  // startPushLoop() in App.tsx owns the flush cycle; this hook only provides
  // UI state (isOnline, syncState, pending count). setupAutoFlush is gone.
  const { isOnline, pending: syncPending, syncState, refreshPending } = useOfflineSync();

  const showToast = useCallback((message: string, fields: string[]) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, fields });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Track saved prescription so we can update/delete it
  const prescriptionSavedMedsSnapRef = useRef<string>("");
  const savedPrescriptionIdRef = useRef<string | null>(null);

  const handleVitalUpdate = (index: number, raw: string) => {
    const validator = VITAL_VALIDATORS[index];
    const value  = validator ? validator.filter(raw) : raw;
    const status = validator ? validator.getStatus(value) : "normal" as const;
    setVitals(prev => {
      const next = prev.map((v, i) => i === index ? { ...v, value, status } : v);
      // Auto-calculate BMI when height or weight changes
      const heightIdx = next.findIndex(v => v.label === "Height");
      const weightIdx = next.findIndex(v => v.label === "Weight");
      const bmiIdx    = next.findIndex(v => v.label === "BMI");
      if (bmiIdx !== -1 && (index === heightIdx || index === weightIdx)) {
        const h = parseFloat(next[heightIdx]?.value ?? "");
        const w = parseFloat(next[weightIdx]?.value ?? "");
        const bmiVal = (!isNaN(h) && h > 0 && !isNaN(w) && w > 0)
          ? String((w / ((h / 100) ** 2)).toFixed(1))
          : "";
        const bmiValidator = VITAL_VALIDATORS[bmiIdx];
        const bmiStatus = bmiVal ? bmiValidator.getStatus(bmiVal) : "normal" as const;
        next[bmiIdx] = { ...next[bmiIdx], value: bmiVal, status: bmiStatus };
      }
      return next;
    });
  };

  // Track whether the form has been changed since mount (don't auto-save on initial load)
  const isDirtyRef = useRef(false);
  const visitIdRef = useRef<string | null>(visitId);
  visitIdRef.current = visitId;

  // ── Track current step for resume routing ───────────────────────────────────
  useEffect(() => {
    if (!visitId) return;
    DiagnosisAPI.updateVisitStep(visitId, "90sdiagnosis").catch(() => {});
  }, [visitId]);

  // ── Resolve display_id → UUID ────────────────────────────────────────────────
  useEffect(() => {
    if (!isPatientDisplayId) return;
    getPatientByDisplayId(rawPatientId)
      .then(p => setPatientId(p.patient_id ?? ""))
      .catch(() => showToast("Patient not found", []));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientDisplayId, rawPatientId]);

  // ── Fetch patient info ───────────────────────────────────────────────────────
  // patientId starts as "" when the URL uses a display_id (PAT-XXXXXX) and is
  // set asynchronously once the display_id→UUID resolution completes, so this
  // effect must re-run on patientId changes.
  useEffect(() => {
    if (!patientId) return;
    DiagnosisAPI.getPatient(patientId).then(setPatient).catch(async (err) => {
      // Every fallback in this file takes ANY error, so a 403 would otherwise
      // restore from IndexedDB the very record the grant was revoked for.
      if (isPermissionDenied(err)) {
        setAccessDenied(actionErrorMessage(err, "You don't have access to this patient."));
        return;
      }
      try {
        const raw = await db.patients.get(patientId);
        if (raw) {
          const p = await unwrapFromStorage<PatientRecord>(raw as unknown as Record<string, unknown>);
          setPatient({ patient_id: p.patient_id, display_id: p.display_id ?? undefined, first_name: (p as any).first_name ?? "", last_name: (p as any).last_name ?? "", date_of_birth: (p as any).date_of_birth ?? "", gender: (p as any).gender ?? "", contact_number: p.contact_number } as PatientInfo);
        }
      } catch { /* non-fatal — header stays blank */ }
    });
  // patientId is "" on mount when resolving a display_id URL; re-run once it resolves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // ── Keep visitId in sync when push engine remaps temp UUID → server UUID ────
  useEffect(() => {
    const handler = (e: Event) => {
      const { tempId, serverId } = (e as CustomEvent<{ tempId: string; serverId: string }>).detail;
      if (visitIdRef.current === tempId) {
        setVisitId(serverId);
        visitIdRef.current = serverId;
        setSearchParams(p => { p.set("visit_id", serverId); return p; }, { replace: true });
      }
    };
    window.addEventListener("kaagazy:visit-remapped", handler);
    return () => window.removeEventListener("kaagazy:visit-remapped", handler);
  }, [setSearchParams]);

  // ── Restore form from DB when visit_id is already in the URL (e.g. after reload) ──
  useEffect(() => {
    const vid = searchParams.get("visit_id");
    if (!vid) return;

    // Restore visit + vitals
    DiagnosisAPI.getVisit(vid).then(res => {
      // reset dirty flag so auto-save doesn't fire on the restored state changes
      isDirtyRef.current = false;
      if (res.visit.chief_complaint) restoreChiefComplaint(res.visit.chief_complaint);
      restoreDentalChart(res.visit.dental_chart);
      if (res.visit.notes) setNotes(res.visit.notes);
      if (res.visit.advice) setAdvice(res.visit.advice);
      if (res.visit.follow_up_date) setFollowUpDate(res.visit.follow_up_date);
      if (res.visit.lab_tests?.length) setLabTests(res.visit.lab_tests.join(", "));
      if (res.vitals) {
        const v = res.vitals;
        setVitals(prev => prev.map(entry => {
          if (entry.label === "Heart Rate" && v.heart_rate != null) return { ...entry, value: String(v.heart_rate) };
          if (entry.label === "Temperature" && v.temperature != null) return { ...entry, value: String(v.temperature) };
          if (entry.label === "Blood Pressure") {
            const sys = v.blood_pressure_systolic;
            const dia = v.blood_pressure_diastolic;
            if (sys != null && dia != null) return { ...entry, value: `${sys}/${dia}` };
            if (sys != null) return { ...entry, value: String(sys) };
            if (dia != null) return { ...entry, value: `/${dia}` };
          }
          if (entry.label === "SpO2" && v.oxygen_saturation != null) return { ...entry, value: String(v.oxygen_saturation) };
          if (entry.label === "Respiration" && v.respiratory_rate != null) return { ...entry, value: String(v.respiratory_rate) };
          if (entry.label === "Height" && v.height != null) return { ...entry, value: String(v.height) };
          if (entry.label === "Weight" && v.weight != null) return { ...entry, value: String(v.weight) };
          if (entry.label === "Head Circumference" && v.head_circumference != null) return { ...entry, value: String(v.head_circumference) };
          if (entry.label === "BMI" && v.bmi != null) return { ...entry, value: String(v.bmi) };
          if (entry.label === "Blood Sugar" && v.blood_sugar != null) return { ...entry, value: String(v.blood_sugar) };
          return entry;
        }));
      }
    }).catch(async (err) => {
      if (isPermissionDenied(err)) {
        setAccessDenied(actionErrorMessage(err, "You don't have access to this visit."));
        return;
      }
      try {
        const rawVisit = await db.visits.get(vid);
        if (rawVisit) {
          const v = await unwrapFromStorage<VisitRecord>(rawVisit as unknown as Record<string, unknown>);
          isDirtyRef.current = false;
          if (v.chief_complaint) restoreChiefComplaint(v.chief_complaint);
          restoreDentalChart(v.dental_chart);
          if (v.notes) setNotes(v.notes);
          if (v.advice) setAdvice(v.advice);
          if (v.follow_up_date) setFollowUpDate(v.follow_up_date);
          if (v.lab_tests?.length) setLabTests(v.lab_tests.join(", "));
        } else {
          setDentalRestoreFailed(true);
        }
        const rawVitals = await db.vitals.where("visit_id").equals(vid).first();
        if (rawVitals) {
          const vt = await unwrapFromStorage<VitalsRecord>(rawVitals as unknown as Record<string, unknown>);
          setVitals(prev => prev.map(entry => {
            if (entry.label === "Heart Rate" && vt.heart_rate != null) return { ...entry, value: String(vt.heart_rate) };
            if (entry.label === "Temperature" && vt.temperature != null) return { ...entry, value: String(vt.temperature) };
            if (entry.label === "Blood Pressure") {
              const sys = vt.blood_pressure_systolic;
              const dia = vt.blood_pressure_diastolic;
              if (sys != null && dia != null) return { ...entry, value: `${sys}/${dia}` };
              if (sys != null) return { ...entry, value: String(sys) };
              if (dia != null) return { ...entry, value: `/${dia}` };
            }
            if (entry.label === "SpO2" && vt.oxygen_saturation != null) return { ...entry, value: String(vt.oxygen_saturation) };
            if (entry.label === "Respiration" && vt.respiratory_rate != null) return { ...entry, value: String(vt.respiratory_rate) };
            if (entry.label === "Height" && vt.height != null) return { ...entry, value: String(vt.height) };
            if (entry.label === "Weight" && vt.weight != null) return { ...entry, value: String(vt.weight) };
            if (entry.label === "BMI" && vt.bmi != null) return { ...entry, value: String(vt.bmi) };
            if (entry.label === "Blood Sugar" && vt.blood_sugar != null) return { ...entry, value: String(vt.blood_sugar) };
            return entry;
          }));
        }
      } catch { setDentalRestoreFailed(true); /* non-fatal: start fresh */ }
    });

    // Restore medicines from prescriptions
    DiagnosisAPI.getPrescriptions(vid).then(prescriptions => {
      isDirtyRef.current = false;
      const allMeds: MedicationItem[] = [];
      if (prescriptions.length > 0) {
        // Backend returns ordered by prescribed_at DESC, so the 0th item is the latest saved prescription draft.
        const latestRx = prescriptions[0];
        for (const med of latestRx.medications) {
          allMeds.push({
            id: Date.now() + Math.random(),
            drug_id: isUuid(med.drug_id) ? med.drug_id : undefined,
            name: med.name,
            dosage: med.dosage,
            frequency: med.frequency,
            route: med.route ?? "",
            duration: med.duration ?? "",
            quantity: med.quantity != null ? String(med.quantity) : "",
            instructions: med.instructions ?? "",
          });
        }
      }
      // Always track the latest prescription id (even if Discontinued with 0 meds)
      if (prescriptions.length > 0) {
        savedPrescriptionIdRef.current = prescriptions[0].prescription_id;
      }
      prescriptionSavedMedsSnapRef.current = JSON.stringify(allMeds);
      if (allMeds.length > 0) {
        setMedications(allMeds);
      }
    }).catch(async (err) => {
      if (isPermissionDenied(err)) {
        setAccessDenied(actionErrorMessage(err, "You don't have access to this visit."));
        return;
      }
      try {
        const rawRxRows = await db.prescriptions.where("visit_id").equals(vid).toArray();
        if (rawRxRows.length > 0) {
          const rxList = await Promise.all(
            rawRxRows.map(r => unwrapFromStorage<PrescriptionRecord>(r as unknown as Record<string, unknown>))
          );
          rxList.sort((a, b) => (b.prescribed_at ?? "").localeCompare(a.prescribed_at ?? ""));
          const latestRx = rxList[0];
          isDirtyRef.current = false;
          const allMeds: MedicationItem[] = (latestRx.medications ?? []).map(med => ({
            id: Date.now() + Math.random(),
            drug_id: isUuid(med.drug_id) ? med.drug_id ?? undefined : undefined,
            name: med.name,
            dosage: med.dosage,
            frequency: med.frequency,
            route: med.route ?? "",
            duration: med.duration ?? "",
            quantity: med.quantity != null ? String(med.quantity) : "",
            instructions: med.instructions ?? "",
          }));
          savedPrescriptionIdRef.current = latestRx.prescription_id;
          prescriptionSavedMedsSnapRef.current = JSON.stringify(allMeds);
          if (allMeds.length > 0) setMedications(allMeds);
        }
      } catch { /* non-fatal — start fresh */ }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildVisitPayload = useCallback(() => ({
    patient_id: patientId,
    doctor_id: doctorId,
    visit_date: new Date().toISOString(),
    visit_type: "OPD",
    chief_complaint: chiefComplaint || undefined,
    notes: notes || undefined,
    advice: advice || undefined,
    follow_up_date: followUpDate || undefined,
    lab_tests: labTests ? labTests.split(",").map(s => s.trim()).filter(Boolean) : undefined,
    vitals: buildVitalsPayload(vitals),
    dental_chart: dentalChartPayload,
  }), [patientId, doctorId, chiefComplaint, notes, advice, followUpDate, labTests, vitals, dentalChartPayload]);

  const saveInFlightRef = useRef<Promise<void> | null>(null);
  const doSave = useCallback(async () => {
    if (!patientId || !doctorId) return;
    // Coalesce concurrent saves — if one is already running, skip
    if (saveInFlightRef.current) return;
    const run = async () => {
    setSaveStatus("saving");
    setSubmitError(null);
    try {
      let vid = visitIdRef.current;
      if (vid) {
        await DiagnosisAPI.patchVisit(vid, {
          chief_complaint: chiefComplaint || undefined,
          notes: notes || undefined,
          advice: advice || undefined,
          follow_up_date: followUpDate || undefined,
          lab_tests: labTests ? labTests.split(",").map(s => s.trim()).filter(Boolean) : undefined,
          vitals: buildVitalsPayload(vitals),
          dental_chart: dentalChartPayload,
        });
      } else {
        const res = await DiagnosisAPI.createVisit(buildVisitPayload() as any);
        vid = res.visit.visit_id;
        setVisitId(vid);
        visitIdRef.current = vid;
        setSearchParams(p => { p.set("visit_id", vid!); return p; }, { replace: true });
        // Save to IndexedDB as "synced" so offline auto-save knows it exists on server
        try {
          await db.visits.put({
            visit_id: vid,
            patient_id: patientId,
            doctor_id: doctorId,
            visit_date: res.visit.visit_date,
            status: res.visit.status,
            chief_complaint: res.visit.chief_complaint,
            notes: res.visit.notes,
            advice: res.visit.advice,
            dental_chart: res.visit.dental_chart,
            sync_status: "synced",
            version: res.visit.sync_version ?? 1,
            local_updated_at: new Date().toISOString(),
          } as any);
        } catch { /* non-fatal */ }
      }

      // Sync medicines: PUT if prescription already exists, POST for first create
      if (vid && JSON.stringify(medications) !== prescriptionSavedMedsSnapRef.current) {
        const medPayload = medications.map(m => ({
          ...(isUuid(m.drug_id) ? { drug_id: m.drug_id } : {}),
          name: m.name,
          dosage: m.dosage,
          frequency: m.frequency,
          route: m.route || undefined,
          duration: m.duration || undefined,
          quantity: m.quantity ? parseInt(m.quantity, 10) : undefined,
          instructions: m.instructions || undefined,
        }));
        if (savedPrescriptionIdRef.current) {
          // Update in-place (empty list → Discontinued, preserves audit row)
          await DiagnosisAPI.updatePrescription(vid, savedPrescriptionIdRef.current, { medications: medPayload, follow_up: followUpDate ? fmtFollowUpDate(followUpDate) : undefined, advice: advice || undefined });
          // Mirror the updated medication list into IndexedDB so the offline
          // PDF + ContinueDiagnosis read paths see the latest edits — the
          // server is authoritative but IDB is the offline source-of-truth.
          // Cast: the online-create path (above) writes prescribed_at as a
          // top-level plaintext field even though the strict OnDisk type
          // only declares it inside the encrypted envelope; mirror that here.
          try {
            const existing = await db.prescriptions.get(savedPrescriptionIdRef.current) as any;
            const envelope = await wrapForStorage("prescription", {
              prescription_id: savedPrescriptionIdRef.current,
              visit_id:        vid,
              prescribed_by:   doctorId,
              prescribed_at:   existing?.prescribed_at ?? new Date().toISOString(),
              status:          existing?.status ?? "Draft",
              medications:     medPayload,
              sync_status:     "synced",
              version:         (existing?.version ?? 1) + 1,
              local_updated_at: new Date().toISOString(),
            });
            await db.prescriptions.put(envelope as never);
          } catch { /* non-fatal */ }
        } else if (medications.length > 0 || followUpDate || advice) {
          // First save — create the prescription (even with no medications so short
          // cases with only advice/follow-up still get a prescription_id for PDF gen)
          const rx = await DiagnosisAPI.createPrescription(vid, { medications: medPayload, follow_up: followUpDate ? fmtFollowUpDate(followUpDate) : undefined, advice: advice || undefined });
          savedPrescriptionIdRef.current = rx.prescription_id;
          // Save to IndexedDB as "synced" so offline knows it exists on server
          try {
            const envelope = await wrapForStorage("prescription", {
              prescription_id: rx.prescription_id,
              visit_id:        vid,
              prescribed_by:   doctorId,
              prescribed_at:   new Date().toISOString(),
              status:          "Draft",
              medications:     medPayload,
              sync_status:     "synced",
              version:         1,
              local_updated_at: new Date().toISOString(),
            });
            await db.prescriptions.put(envelope as never);
          } catch { /* non-fatal */ }
        }
        prescriptionSavedMedsSnapRef.current = JSON.stringify(medications);
      }

      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (err) {
      // If offline or backend unreachable — queue the operation instead of showing an error.
      // Never for a 403: that is a refusal, and queueing it would report "queued".
      if (!isPermissionDenied(err) && isBackendUnreachable(err)) {
        let effectiveVisitId = visitIdRef.current;
        if (effectiveVisitId) {
          // Check if this visit was ever synced to the server. If not, the
          // push engine needs a "create" (POST), not "update" (PATCH).
          const localVisit = await db.visits.get(effectiveVisitId);
          const op = localVisit?.sync_status === "synced" ? "update" : "create";
          if (op === "create") {
            const vp = buildVitalsPayload(vitals);
            await saveVisitWithVitals(
              {
                visit_id:        effectiveVisitId,
                patient_id:      patientId,
                doctor_id:       doctorId,
                visit_date:      new Date().toISOString(),
                status:          "Draft",
                chief_complaint: chiefComplaint || undefined,
                notes:           notes || undefined,
                advice:          advice || undefined,
                follow_up_date:  followUpDate || undefined,
                lab_tests:       labTests ? labTests.split(",").map((s: string) => s.trim()).filter(Boolean) : undefined,
                dental_chart:    dentalChartPayload,
              } satisfies Omit<VisitRecord, keyof SyncFields>,
              {
                vital_id: crypto.randomUUID(),
                visit_id: effectiveVisitId,
                ...vp,
              } satisfies Omit<VitalsRecord, keyof SyncFields>,
              "create",
            );
          } else {
            await enqueue("update", "visit", effectiveVisitId, {
              chief_complaint: chiefComplaint || undefined,
              notes: notes || undefined,
              advice: advice || undefined,
              follow_up_date: followUpDate || undefined,
              lab_tests: labTests ? labTests.split(",").map(s => s.trim()).filter(Boolean) : undefined,
              vitals: buildVitalsPayload(vitals),
              dental_chart: dentalChartPayload,
            } as Record<string, unknown>);
          }
        } else {
          // Assign a client-side UUID so the engine can (a) track this row in
          // db.visits and (b) mark it sync_status='synced' after push succeeds.
          // The server uses this id as the visit_id (offline-first contract).
          const newVisitId = crypto.randomUUID();
          effectiveVisitId = newVisitId;
          const vp = buildVitalsPayload(vitals);
          await saveVisitWithVitals(
            {
              visit_id:        newVisitId,
              patient_id:      patientId,
              doctor_id:       doctorId,
              visit_date:      new Date().toISOString(),
              status:          "Draft",
              chief_complaint: chiefComplaint || undefined,
              notes:           notes || undefined,
              advice:          advice || undefined,
              follow_up_date:  followUpDate || undefined,
              lab_tests:       labTests ? labTests.split(",").map((s: string) => s.trim()).filter(Boolean) : undefined,
              dental_chart:    dentalChartPayload,
            } satisfies Omit<VisitRecord, keyof SyncFields>,
            {
              vital_id: crypto.randomUUID(),
              visit_id: newVisitId,
              ...vp,
            } satisfies Omit<VitalsRecord, keyof SyncFields>,
            "create",
          );
          // Update component state so the next auto-save treats this as an update.
          setVisitId(newVisitId);
          visitIdRef.current = newVisitId;
          setSearchParams(p => { p.set("visit_id", newVisitId); return p; }, { replace: true });
        }
        // Queue prescription alongside the visit so both push in order.
        // visit_id may be a temp UUID here — remapQueueReferences will fix it
        // after the visit push succeeds and returns the server UUID.
        if (effectiveVisitId && medications.length > 0) {
          const medPayload = medications.map(m => ({
            ...(isUuid(m.drug_id) ? { drug_id: m.drug_id } : {}),
            name:         m.name,
            dosage:       m.dosage,
            frequency:    m.frequency,
            route:        m.route || undefined,
            duration:     m.duration || undefined,
            quantity:     m.quantity ? parseInt(m.quantity, 10) : undefined,
            instructions: m.instructions || undefined,
          }));
          if (savedPrescriptionIdRef.current) {
            // Check if prescription was ever synced — if not, use "create".
            // Cast: prescribed_at lives at the top level at runtime even
            // though the strict OnDisk type declares it inside the envelope.
            const localRx = await db.prescriptions.get(savedPrescriptionIdRef.current) as any;
            const rxOp = localRx?.sync_status === "synced" ? "update" : "create";
            await enqueue(rxOp, "prescription", savedPrescriptionIdRef.current, {
              visit_id:        effectiveVisitId,
              prescription_id: savedPrescriptionIdRef.current,
              medications:     medPayload,
            } as Record<string, unknown>);
            // Mirror the offline edit into IDB so ContinueDiagnosis + the
            // offline PDF see the new medication list without waiting for
            // a sync round-trip.
            try {
              const envelope = await wrapForStorage("prescription", {
                prescription_id: savedPrescriptionIdRef.current,
                visit_id:        effectiveVisitId,
                prescribed_by:   doctorId,
                prescribed_at:   localRx?.prescribed_at ?? new Date().toISOString(),
                status:          localRx?.status ?? "Draft",
                medications:     medPayload,
                sync_status:     "pending",
                version:         (localRx?.version ?? 1) + 1,
                local_updated_at: new Date().toISOString(),
              });
              await db.prescriptions.put(envelope as never);
            } catch { /* non-fatal */ }
          } else {
            const newRxId = crypto.randomUUID();
            savedPrescriptionIdRef.current = newRxId;
            await enqueue("create", "prescription", newRxId, {
              prescription_id: newRxId,
              visit_id:        effectiveVisitId,
              medications:     medPayload,
            } as Record<string, unknown>);
            // Persist the freshly-queued create in IDB so the next read in
            // ContinueDiagnosis / offline-PDF actually finds it.
            try {
              const envelope = await wrapForStorage("prescription", {
                prescription_id: newRxId,
                visit_id:        effectiveVisitId,
                prescribed_by:   doctorId,
                prescribed_at:   new Date().toISOString(),
                status:          "Draft",
                medications:     medPayload,
                sync_status:     "pending",
                version:         1,
                local_updated_at: new Date().toISOString(),
              });
              await db.prescriptions.put(envelope as never);
            } catch { /* non-fatal */ }
          }
        }
        await refreshPending();
        setSaveStatus("queued");
        setTimeout(() => setSaveStatus("idle"), 3000);
        return;
      }
      const msg = err instanceof DiagnosisApiError ? err.message : "Save failed";
      setSubmitError(msg);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
    };
    saveInFlightRef.current = run().finally(() => { saveInFlightRef.current = null; });
    return saveInFlightRef.current;
  }, [patientId, doctorId, chiefComplaint, notes, advice, followUpDate, labTests,
      vitals, medications, buildVisitPayload, refreshPending, setSearchParams, dentalChartPayload]);

  // ── Debounced auto-save (1.5 s after last change) ───────────────────────────
  useEffect(() => {
    if (!isDirtyRef.current) { isDirtyRef.current = true; return; }
    const timer = setTimeout(() => { doSave(); }, 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiefComplaint, notes, advice, followUpDate, labTests, vitals, medications, toothFindings, planItems, dentition]);

  const handleSaveDraft = () => { doSave(); };

  const handleContinue = async () => {
    if (isSubmitting) return;

    // ── Validate required fields before proceeding ────────────────────────────
    const missing: string[] = [];
    if (!chiefComplaint.trim())                         missing.push("Chief Complaint");
    if (!notes.trim())                                  missing.push("Diagnosis");
    if (missing.length > 0) {
      showToast("Please fill in the required fields:", missing);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      let vid = visitId;
      if (!vid) {
        if (!patientId || !doctorId) { navigate("/continue-diagnosis"); return; }
        const res = await DiagnosisAPI.createVisit(buildVisitPayload() as any);
        vid = res.visit.visit_id;
        setVisitId(vid);
      } else {
        await DiagnosisAPI.patchVisit(vid, {
          chief_complaint: chiefComplaint || undefined,
          notes: notes || undefined,
          advice: advice || undefined,
          follow_up_date: followUpDate || undefined,
          lab_tests: labTests ? labTests.split(",").map(s => s.trim()).filter(Boolean) : undefined,
          vitals: buildVitalsPayload(vitals),
          dental_chart: dentalChartPayload,
        });
      }
      if (JSON.stringify(medications) !== prescriptionSavedMedsSnapRef.current) {
        const medPayload = medications.map(m => ({
          ...(isUuid(m.drug_id) ? { drug_id: m.drug_id } : {}),
          name: m.name,
          dosage: m.dosage,
          frequency: m.frequency,
          route: m.route || undefined,
          duration: m.duration || undefined,
          quantity: m.quantity ? parseInt(m.quantity, 10) : undefined,
          instructions: m.instructions || undefined,
        }));
        if (savedPrescriptionIdRef.current) {
          await DiagnosisAPI.updatePrescription(vid!, savedPrescriptionIdRef.current, { medications: medPayload, follow_up: followUpDate ? fmtFollowUpDate(followUpDate) : undefined, advice: advice || undefined });
        } else {
          // Always create a prescription on submit (even with no medications) so
          // ContinueDiagnosis can generate a PDF for short cases (advice-only visits).
          const rx = await DiagnosisAPI.createPrescription(vid!, { medications: medPayload, follow_up: followUpDate ? fmtFollowUpDate(followUpDate) : undefined, advice: advice || undefined });
          savedPrescriptionIdRef.current = rx.prescription_id;
        }
        prescriptionSavedMedsSnapRef.current = JSON.stringify(medications);
      } else if (!savedPrescriptionIdRef.current) {
        // No medication change but also no prescription yet — create one so the
        // review page always has a prescription_id to generate a PDF against.
        const rx = await DiagnosisAPI.createPrescription(vid!, {
          medications: [],
          follow_up: followUpDate ? fmtFollowUpDate(followUpDate) : undefined,
          advice: advice || undefined,
        });
        savedPrescriptionIdRef.current = rx.prescription_id;
      }
      navigate(`/continue-diagnosis?visit_id=${vid}&patient_id=${rawPatientId}&doctor_id=${doctorId}`);
    } catch (err) {
      if (!isPermissionDenied(err) && isBackendUnreachable(err)) {
        let vid = visitId;
        try {
          if (vid) {
            // Check if this visit was ever synced. If not, use "create" so
            // the push engine POSTs instead of PATCHing a non-existent visit.
            const localVisit = await db.visits.get(vid);
            const op = localVisit?.sync_status === "synced" ? "update" : "create";
            if (op === "create") {
              const vp = buildVitalsPayload(vitals);
              await saveVisitWithVitals(
                {
                  visit_id:        vid,
                  patient_id:      patientId!,
                  doctor_id:       doctorId!,
                  visit_date:      new Date().toISOString(),
                  status:          "Draft",
                  chief_complaint: chiefComplaint || undefined,
                  notes:           notes || undefined,
                  advice:          advice || undefined,
                  follow_up_date:  followUpDate || undefined,
                  lab_tests:       labTests ? labTests.split(",").map((s: string) => s.trim()).filter(Boolean) : undefined,
                  dental_chart:    dentalChartPayload,
                } satisfies Omit<VisitRecord, keyof SyncFields>,
                {
                  vital_id: crypto.randomUUID(),
                  visit_id: vid,
                  ...vp,
                } satisfies Omit<VitalsRecord, keyof SyncFields>,
                "create",
              );
            } else {
              await enqueue("update", "visit", vid, {
                chief_complaint: chiefComplaint || undefined,
                notes: notes || undefined,
                advice: advice || undefined,
                follow_up_date: followUpDate || undefined,
                lab_tests: labTests ? labTests.split(",").map((s: string) => s.trim()).filter(Boolean) : undefined,
                vitals: buildVitalsPayload(vitals),
                dental_chart: dentalChartPayload,
              } as Record<string, unknown>);
            }
          } else {
            vid = crypto.randomUUID();
            const vp = buildVitalsPayload(vitals);
            await saveVisitWithVitals(
              {
                visit_id:        vid,
                patient_id:      patientId!,
                doctor_id:       doctorId!,
                visit_date:      new Date().toISOString(),
                status:          "Draft",
                chief_complaint: chiefComplaint || undefined,
                notes:           notes || undefined,
                advice:          advice || undefined,
                follow_up_date:  followUpDate || undefined,
                lab_tests:       labTests ? labTests.split(",").map((s: string) => s.trim()).filter(Boolean) : undefined,
                dental_chart:    dentalChartPayload,
              } satisfies Omit<VisitRecord, keyof SyncFields>,
              {
                vital_id: crypto.randomUUID(),
                visit_id: vid,
                ...vp,
              } satisfies Omit<VitalsRecord, keyof SyncFields>,
              "create",
            );
            setVisitId(vid);
          }
          // Save prescription to IndexedDB so ContinueDiagnosis can read it offline
          if (medications.length > 0) {
            const rxId = savedPrescriptionIdRef.current || crypto.randomUUID();
            savedPrescriptionIdRef.current = rxId;
            const localRx = await db.prescriptions.get(rxId);
            const rxOp2 = localRx?.sync_status === "synced" ? "update" : "create";
            await putAndQueue<PrescriptionRecord>(
              "prescription",
              rxId,
              {
                prescription_id: rxId,
                visit_id:        vid!,
                prescribed_by:   doctorId!,
                prescribed_at:   new Date().toISOString(),
                status:          "Draft",
                medications:     medications.map(m => ({
                  medication_id: crypto.randomUUID(),
                  drug_id:       m.drug_id ?? null,
                  name:          m.name,
                  dosage:        m.dosage,
                  frequency:     m.frequency,
                  route:         m.route || null,
                  duration:      m.duration || null,
                  quantity:      m.quantity ? parseInt(m.quantity, 10) : null,
                  instructions:  m.instructions || null,
                })),
              },
              rxOp2,
            );
          }
          navigate(`/continue-diagnosis?visit_id=${vid}&patient_id=${rawPatientId}&doctor_id=${doctorId}`);
          return;
        } catch {
          setSubmitError("Failed to save offline");
        }
      } else {
        const msg = err instanceof DiagnosisApiError ? err.message : "Submit failed";
        setSubmitError(msg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { label: "Chief Complaint", done: chiefComplaint.trim().length > 0 },
    { label: "Diagnosis",       done: notes.trim().length > 0 },
  ];
  const progress = Math.round((steps.filter(s => s.done).length / steps.length) * 100);

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-[#f5f5f7]">
        <Sidebar />
        <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen">
          <AccessDenied label="this visit" detail={accessDenied} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      {/* ── Validation Toast ── */}
      {toast && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] flex items-start gap-3 bg-red-600 text-white px-4 py-3 rounded-2xl shadow-xl max-w-sm w-full mx-4 animate-[fadeIn_0.2s_ease]">
          <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-semibold">{toast.message}</p>
            <ul className="mt-1 space-y-0.5">
              {toast.fields.map(f => (
                <li key={f} className="text-xs opacity-90">• {f}</li>
              ))}
            </ul>
          </div>
          <button onClick={() => setToast(null)} className="opacity-70 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
      <AIScribeConsentModal
        isOpen={showScribeConsent}
        onConsent={async () => {
          setShowScribeConsent(false);
          if (visitId) {
            try { await apiRequest(`/visits/${visitId}/ai-consent`, { method: "POST" }); }
            catch { /* non-fatal */ }
          }
          setShowScribeModal(true);
        }}
        onDecline={() => setShowScribeConsent(false)}
      />
      {showScribeModal && (
        <AIScribeModal
          visitId={visitId || ""}
          onClose={() => setShowScribeModal(false)}
          onApply={(scribeResult) => {
            setShowScribeModal(false);

            // Also fill fields on this page so they're visible if user stays
            if (scribeResult.soap_note && typeof scribeResult.soap_note === "object" && scribeResult.soap_note.chief_complaint) {
              // Charted teeth live in the chart and the Diagnosis line, not here.
              setChiefComplaint(scribeResult.soap_note.chief_complaint);
            }
            if (scribeResult.prescription_document.advice) {
              setAdvice(scribeResult.prescription_document.advice);
            }
            if (scribeResult.prescription_document.investigations?.length) {
              setLabTests(scribeResult.prescription_document.investigations.join(", "));
            }
            if (scribeResult.prescription_document.medications?.length) {
              const aiMeds: MedicationItem[] = scribeResult.prescription_document.medications.map((med, i) => ({
                id: Date.now() + i,
                name: med.drug_name,
                dosage: med.dosage || "",
                frequency: med.frequency || "OD",
                route: med.route || "oral",
                duration: med.duration_days ? `${med.duration_days} days` : "",
                quantity: med.total_quantity ? String(med.total_quantity) : "",
                instructions: [
                  med.timing || "",
                  med.instructions || "",
                  med.allergy_conflict ? "ALLERGY CONFLICT" : "",
                ].filter(Boolean).join(" | "),
              }));
              setMedications(aiMeds);
            }

            // Navigate to SOAP pad — pass AI Scribe data via state for instant fill (no re-fetch)
            navigate(`/soap-diagnosis-pad?visit_id=${visitId || ""}&patient_id=${rawPatientId}&doctor_id=${doctorId}`, {
              state: { aiScribeResult: scribeResult },
            });
          }}
        />
      )}
      {showAddMedicine && (
        <AddMedicineModal
          key={editingMedicine?.id ?? "new"}
          initialData={editingMedicine}
          onClose={() => {
            setShowAddMedicine(false);
            setEditingMedicine(null);
          }}
          onAdd={(med) => {
            if (editingMedicine) {
              setMedications((prev) =>
                prev.map((m) => (m.id === editingMedicine.id ? { ...med, id: editingMedicine.id } : m))
              );
            } else {
              setMedications((prev) => [...prev, { ...med, id: Date.now() }]);
            }
            setShowAddMedicine(false);
            setEditingMedicine(null);
          }}
        />
      )}
      {/* Dental FDI chart modal */}
      <ToothChart
        isOpen={showDentalFinding}
        onClose={() => setShowDentalFinding(false)}
        findings={toothFindings}
        onChange={setToothFindings}
      />
      {/* S4 — Upgrade Prompt Modal for AI Scribe */}
      {showUpgradeModal && (
        <UpgradePromptModal
          feature="ai_scribe"
          featureLabel="AI Scribe"
          variant={aiScribeGate === "expired" ? (aiScribeFlag?.is_trial ? "trial_expired" : "subscription_expired") : "feature_not_enabled"}
          expiredDate={aiScribeFlag?.expires_at}
          isAdmin={isAdmin}
          onDismiss={() => setShowUpgradeModal(false)}
          onUpgradeClick={() => { setShowUpgradeModal(false); handleUpgradeClick(); }}
          onNotifyAdmin={() => { setShowUpgradeModal(false); handleNotifyAdmin(); }}
        />
      )}
      <Sidebar />

      {/* Content offset: desktop shifts right by sidebar width; mobile adds top space for hamburger */}
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out min-h-screen flex flex-col">
      <div className="flex flex-col overflow-hidden min-h-0 flex-1">

        {/* ── Header ── */}
        <div className="px-4 pt-14 pb-3 lg:pt-3 border-b border-slate-100 flex-shrink-0">
          {/* Row 1: icon + title + Continue button */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm ${BLUE_GRADIENT}`}>
                <IcStethoscope />
              </div>
              <h1 className="text-[15px] font-bold text-slate-900 leading-tight truncate">Diagnosis Pad</h1>
            </div>
            <button
              onClick={handleContinue}
              disabled={isSubmitting}
              className={`flex items-center gap-1.5 px-3 min-h-[44px] sm:px-5 sm:py-2 rounded-xl text-white text-xs sm:text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50 flex-shrink-0 ml-2 ${BLUE_GRADIENT}`}
            >
              <IcSend />{isSubmitting ? "Saving…" : "Continue"}
            </button>
          </div>

          {/* Row 2: patient details */}
          <p className="text-xs text-slate-400 mb-2 pl-[46px] sm:pl-[52px]">
            {patient
              ? `Patient: ${patient.first_name} ${patient.last_name}\u00a0•\u00a0Age: ${calcAge(patient.date_of_birth)}\u00a0•\u00a0ID: ${patient.display_id ?? fmtPatientId(patient.patient_id)}`
              : patientId ? `ID: ${fmtPatientId(patientId)}` : "Loading patient…"}
          </p>

          {/* Row 3: AI Scribe + Save Draft + offline indicator */}
          <div className="flex items-center justify-end gap-2 mb-3 flex-wrap">
            {!aiScribeHidden && (
              <button onClick={async () => {
                if (aiScribeLocked) { setShowUpgradeModal(true); return; }
                if (!visitId) {
                  if (!patientId || !doctorId) return;
                  await doSave();
                  await new Promise(r => setTimeout(r, 300));
                  if (!visitIdRef.current) return;
                }
                setShowScribeConsent(true);
              }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-purple-200 bg-purple-50 text-purple-600 text-xs font-medium hover:bg-purple-100 transition-colors">
                <IcSparkle />AI Scribe
                <FeatureGateChip
                  state={aiScribeGate === "active" ? "hidden" : aiScribeGate}
                  daysLeft={aiScribeFlag?.days_left}
                />
              </button>
            )}
            <button
              onClick={handleSaveDraft}
              disabled={saveStatus === "saving"}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors disabled:opacity-50 ${
                saveStatus === "saved"
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : saveStatus === "queued"
                  ? "border-amber-300 bg-amber-50 text-amber-700"
                  : saveStatus === "error"
                  ? "border-red-300 bg-red-50 text-red-700"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              <IcSave />
              {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved!" : saveStatus === "queued" ? "Queued" : saveStatus === "error" ? "Error" : "Save Draft"}
            </button>

            {/* Offline / sync status pill */}
            {!isOnline && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-300 text-xs font-medium text-amber-700">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                Offline{syncPending > 0 ? ` · ${syncPending} queued` : ""}
              </span>
            )}
            {isOnline && syncState === "syncing" && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-50 border border-sky-300 text-xs font-medium text-sky-700">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 inline-block animate-pulse" />
                Syncing…
              </span>
            )}
            {isOnline && syncState === "conflict" && (
              <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-50 border border-red-300 text-xs font-medium text-red-700">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                Sync conflict
              </span>
            )}
          </div>

          {/* Error banner */}
          {submitError && (
            <div className="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
              {submitError}
            </div>
          )}

          {/* Progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-slate-700">Completion Progress</span>
            <span className="text-xs font-semibold text-blue-600">{progress}%</span>
          </div>
          <div className="h-[6px] bg-slate-200 rounded-full overflow-hidden mb-2.5">
            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center gap-5">
            {steps.map((step) => (
              <div key={step.label} className="flex items-center gap-1 text-xs text-slate-500">
                {step.done ? <IcCheckCircle /> : <IcInfoCircle />}
                {step.label}
              </div>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-col xl:flex-row gap-3 flex-1 min-h-0 p-3 overflow-y-auto xl:overflow-hidden">

          {/* Left: Vitals Overview + Allergies */}
          <div className="w-full xl:w-72 xl:flex-shrink-0 xl:overflow-y-auto flex flex-col gap-3">
            <VitalsOverview vitals={vitals} onUpdate={handleVitalUpdate} />
            <AllergiesPanel patientId={patientId} guardsPrescription />
          </div>

          {/* Center: Chief Complaint + AI Diagnostic + Clinical Notes stacked — widest column */}
          <div className="w-full xl:flex-1 flex flex-col gap-3 xl:overflow-y-auto min-w-0">
            <ChiefComplaintPanel
              value={chiefComplaint}
              onChange={setChiefComplaint}
              onDentalFindingClick={isDentist && !dentalPlanOn ? () => setShowDentalFinding(true) : undefined}
              onScribeClick={aiScribeHidden ? undefined : async () => {
              if (aiScribeLocked) { setShowUpgradeModal(true); return; }
              if (!visitId) {
                if (!patientId || !doctorId) return;
                await doSave();
                await new Promise(r => setTimeout(r, 300));
                if (!visitIdRef.current) return;
              }
              setShowScribeConsent(true);
            }} />
            {dentalPlanOn && (
              <>
                {dentalRestoreFailed && (
                  <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    This visit's saved dental chart could not be loaded. Keep charting: it will be combined with the saved chart and saved once you are back online.
                  </p>
                )}
                <DentalChartPanel
                  findings={dentalFindings}
                  onChange={(f) => setToothFindings(f as ToothFinding[])}
                  dentition={dentition}
                  onDentitionChange={setDentition}
                />
                <TreatmentPlanPanel
                  items={planItems}
                  onChange={setPlanItems}
                  findings={dentalFindings}
                  favouriteNames={dentalTemplates.favouriteNames}
                  onSaveTemplate={dentalTemplates.save}
                  templates={dentalTemplates.templates}
                  canUseTemplates={canUseTemplates}
                />
              </>
            )}
            <SelectedDiagnosesPanel
              diagnoses={selectedDiagnoses}
              onRemove={(dx) => setSelectedDiagnoses((prev) => prev.filter((d) => d.icd !== dx.icd))}
            />

            <ClinicalNotesPanel value={notes} onChange={setNotes} />

            {/* Visit Notes — attributed, versioned, multi-doctor thread. Needs a
                saved visit_id to exist (entries FK to visits), so it only renders
                once the draft visit has been saved at least once. Named "Visit
                Notes" (not "Doctor Notes") to avoid colliding with the unrelated,
                patient-level "Doctor Notes" tab from feature/doctor-notes-attributed-log. */}
            {visitId && <VisitNotesPanel visitId={visitId} />}
          </div>

          {/* Right: Medicine Kits, Prescription, Lab Tests, Advice, Follow-Up */}
          <div className="w-full xl:w-72 xl:flex-shrink-0 flex flex-col gap-3 xl:overflow-y-auto">

            {/* Medicine Kits */}
            <MedicineKitsPanel />

            {/* Medicines */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-[15px] font-bold text-slate-800">
                  Medicines ({medications.length})
                </h2>
                <button
                  onClick={() => {
                    setEditingMedicine(null);
                    setShowAddMedicine(true);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-white text-sm font-semibold shadow-sm hover:opacity-90 transition-opacity ${BLUE_GRADIENT}`}
                  aria-label="Add medicine"
                >
                  <IcPlus />
                  Add
                </button>
              </div>

              {medications.map((med) => (
                <div key={med.id} className="border border-slate-200 rounded-xl px-4 py-3 flex items-start justify-between gap-2 hover:border-slate-300 transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-slate-800">{med.name} {med.dosage}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {[med.frequency, med.route, med.duration].filter(Boolean).join(" • ")}
                    </p>
                    {med.instructions && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{med.instructions}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => {
                        setEditingMedicine(med);
                        setShowAddMedicine(true);
                      }}
                      className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit medicine"
                      aria-label={`Edit ${med.name}`}
                    >
                      <IcEdit />
                    </button>
                    <button
                      onClick={() => setMedications((prev) => prev.filter((m) => m.id !== med.id))}
                      className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove medicine"
                      aria-label={`Remove ${med.name}`}
                    >
                      <IcX />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Lab Tests */}
            <LabTestsPanel value={labTests} onChange={setLabTests} />

            {/* Advice */}
            <AdvicePanel value={advice} onChange={setAdvice} />

            {/* Follow-Up */}
            <FollowUpPanel value={followUpDate} onChange={setFollowUpDate} />

            {/* Documents attached to this visit */}
            <VisitDocumentsPanel patientId={patientId} visitId={visitId} />

          </div>

        </div>
      </div>
      </div>
    </div>
  );
};

export default NinetysDiagnosis;
